import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Bot,
  Boxes,
  Braces,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock3,
  Code2,
  Copy,
  CornerDownRight,
  FileCheck2,
  FlaskConical,
  History,
  Lightbulb,
  Milestone,
  Search,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { Fragment, memo, useState, type ReactNode } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { copyText } from "./clipboard.ts";
import type {
  AiAnalysisDetails,
  ExecutionReadback,
  Experiment,
  MapChangelogEntry,
  OptimizationRecommendations,
  RunExecutionDetails,
  ActionEntity,
} from "./types.ts";
import { automaticTargetLabel, entityDisplayLabel, extractAsinFromAction, shortTargetingTypeLabel } from "./targeting.ts";
import { ExperimentCard } from "./ExperimentCard.tsx";

function sanitizeAiSummary(rawSummary: string | null | undefined, fallbackBody?: string | null): string {
  if (!rawSummary) return "AI 优化方案";
  const s = rawSummary.trim();
  if (s.startsWith("{") || s.startsWith("[") || s.includes("reformatting") || s.startsWith("Considering ")) {
    if (fallbackBody) {
      const lines = fallbackBody.split("\n").map((l) => l.replace(/^[#>\s*\-_【】]+/, "").trim()).filter(Boolean);
      const cleanLine = lines.find((l) => l.length > 3 && !l.startsWith("{") && !l.startsWith("[") && !l.startsWith("Considering ") && !l.startsWith("The previous "));
      if (cleanLine) return cleanLine.slice(0, 120);
    }
    return "AI 优化方案已生成";
  }
  return s;
}

function sanitizeAiMarkdown(rawMarkdown: string | null | undefined): string {
  if (!rawMarkdown) return "";
  let text = rawMarkdown.trim();
  text = text
    .replace(/^\{[^{}]*?(?:reformatting|contract|adhere|thought|previous output)[^{}]*?\}\s*/i, "")
    .replace(/<(?:thought|thinking)>[\s\S]*?<\/(?:thought|thinking)>/gi, "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .trim();

  if (text.startsWith("{") && (text.includes('"final_recommendation_markdown"') || text.includes('"body_markdown"') || text.includes('"artifact"'))) {
    try {
      const parsed = JSON.parse(text);
      const root = parsed.data || parsed.result || parsed.response || parsed;
      const md = root.artifact?.final_recommendation_markdown || root.artifact?.body_markdown || root.final_recommendation_markdown || root.body_markdown;
      if (typeof md === "string" && md.trim()) return md.trim();
    } catch { /* ignore */ }
  }

  const headerIndex = text.search(/(?:^|\n)(?:#|【|##)/);
  if (headerIndex > 0 && text.slice(0, headerIndex).includes("{")) {
    text = text.slice(headerIndex).trim();
  }

  return text;
}

export function AiAnalysisView({ data }: { data: AiAnalysisDetails | null }) {
  if (!data) return <AuditLoading />;
  if (!data.available || !data.analysis)
    return (
      <AuditEmpty
        icon={<Search />}
        title="本轮没有 AI 分析"
        detail="仅运行模式完成后将在这里显示完整分析正文"
      />
    );
  return (
    <div className="audit-view">
      <section className="analysis-overview">
        <span className="audit-kicker"><Search />AI ANALYSIS</span>
        <h2>{sanitizeAiSummary(data.analysis.summary, data.analysis.body_markdown)}</h2>
        <div className="ai-recommendation-meta">
          <span>模型 <code>{data.analysis.model_id}</code></span>
          <span>生成 <time>{dateTime(data.analysis.generated_at)}</time></span>
          <span>响应 <code>{shortHash(data.analysis.response_sha256)}</code></span>
          <span>Artifact <code>{shortId(data.analysis.artifact_id)}</code></span>
        </div>
      </section>
      <article className="ai-recommendation-document analysis-document">
        <MarkdownDocument>{sanitizeAiMarkdown(data.analysis.body_markdown)}</MarkdownDocument>
      </article>
    </div>
  );
}

export function OptimizationAdviceView({
  data,
  currency,
  taskType,
  defaultEvidenceExpanded = false,
  defaultFinalRecommendationExpanded = false,
}: {
  data: OptimizationRecommendations | null;
  currency?: string | null;
  taskType?: string | null;
  defaultEvidenceExpanded?: boolean;
  defaultFinalRecommendationExpanded?: boolean;
}) {
  if (!data) return <AuditLoading />;
  if (!data.available || !data.plan || !data.recommendation)
    return (
      <AuditEmpty
        icon={<Lightbulb />}
        title="本轮没有优化建议"
        detail="该 Run 未生成 Optimization Plan"
      />
    );
  const recommendation = data.recommendation;
  const entityIds = data.actions.flatMap((action) => entityIdsForAction(action));
  const proposed = data.actions.filter(
    (action) => action.status === "PROPOSED",
  ).length;
  const isAutonomous = taskType
    ? ["AUTONOMOUS_LAUNCH", "DAILY_SENTINEL", "ADAPTIVE_EVALUATION", "MILESTONE_TRANSITION"].includes(taskType)
    : Boolean(recommendation.adaptive_evaluation || (recommendation as any).is_early_probe !== undefined);

  const isAdaptiveTask = taskType === "ADAPTIVE_EVALUATION" || Boolean(recommendation.adaptive_evaluation);

  return (
    <div className="audit-view">
      <section className="audit-overview">
        <div className="audit-overview-copy">
          <span className="audit-kicker">
            <Lightbulb />
            OPTIMIZATION PLAN
          </span>
          <h2>{withoutEntityIds(sanitizeAiSummary(recommendation.summary, recommendation.final_recommendation?.body_markdown), entityIds)}</h2>
          <p>{withoutEntityIds(recommendation.objective, entityIds)}</p>
        </div>
        <div className="audit-facts">
          <Fact
            label="来源"
            value={recommendation.source === "AI" ? "AI" : "规则引擎"}
          />
          <Fact label="数据窗口" value={recommendation.data_window} />
          <Fact label="建议动作" value={String(data.actions.length)} />
          <Fact label="待审批" value={String(proposed)} />
        </div>
        <div className="audit-identity">
          <AuditStatus value={data.plan.status} />
          <span>
            Plan <code>{shortId(data.plan.plan_id)}</code>
          </span>
          <span>
            Hash <code>{shortHash(data.plan.plan_hash)}</code>
          </span>
        </div>
      </section>

      {recommendation.final_recommendation && (
        <AiFinalRecommendation
          recommendation={recommendation.final_recommendation}
          entityIds={entityIds}
          defaultExpanded={defaultFinalRecommendationExpanded || Boolean(data.evaluated_experiments?.length) || isAutonomous}
        />
      )}

      {(taskType === "DAILY_SENTINEL" || (recommendation as any).sentinel_report) && (
        <DailySentinelSection
          report={(recommendation as any).sentinel_report}
          currency={currency}
          defaultExpanded={true}
        />
      )}

      {isAdaptiveTask && recommendation.adaptive_evaluation && (
        <AdaptiveEvaluationSection
          report={recommendation.adaptive_evaluation}
          currency={currency}
          defaultExpanded={true}
        />
      )}

      {(taskType === "MILESTONE_TRANSITION" || (recommendation as any).milestone_report) && (
        <MilestoneTransitionSection
          report={(recommendation as any).milestone_report}
          currency={currency}
          defaultExpanded={true}
        />
      )}

      {!isAutonomous && data.evaluated_experiments && data.evaluated_experiments.length > 0 && (
        <ExperimentReviewSection
          experiments={data.evaluated_experiments}
          defaultExpanded={true}
        />
      )}

      {data.actions.length > 0 && (
        <section className="audit-list" aria-label="优化动作">
        {data.actions.map((action) => (
          <article className="recommendation-action" key={action.action_id}>
            <header className="audit-action-header">
              <span className="audit-ordinal">
                {String(action.ordinal + 1).padStart(2, "0")}
              </span>
              <div>
                <h3>
                  {resolveActionCardHeader(action)}
                </h3>
                <p>
                  {(() => {
                    const cName = action.campaign_name?.trim();
                    const cId = action.campaign_id?.trim();
                    if (cName && cName !== cId && !/^\d+$/.test(cName)) {
                      return cName;
                    }
                    const entityCampName = (action.entity as any)?.campaign_name || (action.entity as any)?.parent_name || (action.entity as any)?.campaignName;
                    if (entityCampName && !/^\d+$/.test(String(entityCampName).trim())) {
                      return String(entityCampName).trim();
                    }
                    return cName || cId || "未关联 Campaign";
                  })()}
                  {action.entity && <span className="action-entity-meta"> · {entityMetaLabel(action.entity, action.resource_type)}</span>}
                </p>
              </div>
              <div className="audit-action-status">
                <AuditStatus value={action.risk} />
                <AuditStatus value={action.status} />
              </div>
            </header>
            {(() => {
              const actionSymbol = resolveCurrencySymbol(currency, action.evidence, action.campaign_name, action.decision_reason) || (currency ? getCurrencySymbol(currency) : "€");
              if (isCreateCampaign(action)) {
                const rec = ((action.proposed_value || action.constrained_value || (action as any).payload) ?? {}) as Record<string, unknown>;
                const campName = String(rec.name ?? action.campaign_name ?? "新建广告活动");
                const budget = Number(rec.daily_budget ?? rec.dailyBudget ?? 10.00);
                const targeting = String(rec.targeting ?? rec.targetingType ?? "MANUAL");
                const targetingLabel = targeting === "MANUAL" ? "手动投放 (MANUAL)" : targeting === "AUTO" ? "自动投放 (AUTO)" : targeting;
                return (
                  <div className="recommendation-values negative-action">
                    <div className="value-cell emphasized" style={{ flex: "1 1 280px" }}>
                      <span className="value-cell-label">新建广告活动</span>
                      <code className="value-cell-code" title={campName}>
                        {campName}
                      </code>
                    </div>
                    <div className="value-cell" style={{ flex: "0 0 160px", minWidth: "130px" }}>
                      <span className="value-cell-label">初始日预算</span>
                      <code className="value-cell-code micro-val">
                        {actionSymbol}{budget.toFixed(2)} / 日
                      </code>
                    </div>
                    <div className="value-cell" style={{ flex: "0 0 160px", minWidth: "130px" }}>
                      <span className="value-cell-label">投放模式</span>
                      <code className="value-cell-code micro-val">
                        {targetingLabel}
                      </code>
                    </div>
                  </div>
                );
              }
              if (isCreateAdGroup(action)) {
                const rec = ((action.proposed_value || action.constrained_value || (action as any).payload) ?? {}) as Record<string, unknown>;
                const adGroupName = String(rec.ad_group_name ?? rec.adGroupName ?? "新建广告组");
                const defaultBid = Number(rec.default_bid ?? rec.defaultBid ?? 1.00);
                const state = String(rec.status ?? rec.state ?? "ENABLED");
                return (
                  <div className="recommendation-values negative-action">
                    <div className="value-cell emphasized" style={{ flex: "1 1 280px" }}>
                      <span className="value-cell-label">新建广告组名称</span>
                      <code className="value-cell-code" title={adGroupName}>
                        {adGroupName}
                      </code>
                    </div>
                    <div className="value-cell" style={{ flex: "0 0 160px", minWidth: "130px" }}>
                      <span className="value-cell-label">默认竞价 (Default Bid)</span>
                      <code className="value-cell-code micro-val">
                        {actionSymbol}{defaultBid.toFixed(2)}
                      </code>
                    </div>
                    <div className="value-cell" style={{ flex: "0 0 140px", minWidth: "120px" }}>
                      <span className="value-cell-label">初始状态</span>
                      <code className="value-cell-code micro-val">
                        {state === "ENABLED" ? "开启 (ENABLED)" : state}
                      </code>
                    </div>
                  </div>
                );
              }
              if (isCreateProductAd(action)) {
                const rec = ((action.proposed_value || action.constrained_value || (action as any).payload) ?? {}) as Record<string, unknown>;
                const ev = (action.evidence ?? {}) as Record<string, unknown>;
                const asin = String(rec.asin ?? ev.asin ?? "ASIN");
                const sku = String(rec.sku ?? ev.sku ?? "-");
                const adGroupName = String(rec.adGroupName ?? rec.ad_group_name ?? (action as any).payload?.adGroupName ?? "-");
                const state = String(rec.status ?? rec.state ?? "ENABLED");
                return (
                  <div className="recommendation-values negative-action">
                    <div className="value-cell emphasized" style={{ flex: "1 1 240px" }}>
                      <span className="value-cell-label">添加推广商品</span>
                      <code className="value-cell-code">
                        ASIN: {asin} {sku !== "-" ? `· SKU: ${sku}` : ""}
                      </code>
                    </div>
                    {adGroupName !== "-" && (
                      <div className="value-cell" style={{ flex: "1 1 260px" }}>
                        <span className="value-cell-label">目标广告组</span>
                        <code className="value-cell-code micro-val" title={adGroupName}>
                          {adGroupName}
                        </code>
                      </div>
                    )}
                    <div className="value-cell" style={{ flex: "0 0 140px", minWidth: "120px" }}>
                      <span className="value-cell-label">推广状态</span>
                      <code className="value-cell-code micro-val">
                        {state === "ENABLED" ? "开启 (ENABLED)" : state}
                      </code>
                    </div>
                  </div>
                );
              }
              if (isCreateAsinTarget(action)) {
                const asin = extractAsinFromTargetAction(action) ?? "投放目标";
                const bid = extractBidFromTargetAction(action);
                const title = extractNegativeProductTitle(action);
                return (
                  <div className="recommendation-values negative-action">
                    <div className="value-cell emphasized" style={{ flex: "0 0 200px", minWidth: "160px" }}>
                      <span className="value-cell-label">新增商品投放 (PAT)</span>
                      <code className="value-cell-code">
                        ASIN {asin}
                      </code>
                    </div>
                    <div className="value-cell" style={{ flex: "0 0 140px", minWidth: "120px" }}>
                      <span className="value-cell-label">建议竞价</span>
                      <code className="value-cell-code micro-val">
                        {bid != null ? `${actionSymbol}${Number(bid).toFixed(2)}` : "-"}
                      </code>
                    </div>
                    <div className="value-cell negative-title-cell" style={{ flex: "1 1 300px" }}>
                      <span className="value-cell-label">ASIN 标题</span>
                      <span className="negative-title-text" title={title ?? "暂无商品标题"}>
                        {title ?? "暂无商品标题"}
                      </span>
                    </div>
                  </div>
                );
              }
              if (isCreateKeyword(action)) {
                const kw = extractKeywordFromAction(action);
                const rec = (action.proposed_value ?? {}) as Record<string, unknown>;
                const ev = (action.evidence ?? {}) as Record<string, unknown>;
                const tos = rec.top_of_search_multiplier_pct ?? ev.top_of_search_multiplier_pct;
                const peakBid = ev.effective_top_bid ?? (kw.bid != null && tos != null ? Number((kw.bid * (1 + Number(tos) / 100)).toFixed(2)) : null);
                return (
                  <div className="recommendation-values negative-action">
                    <div className="value-cell emphasized" style={{ flex: "1 1 240px" }}>
                      <span className="value-cell-label">{tos != null ? "新增关键词投放" : "新增关键词"}</span>
                      <code className="value-cell-code">
                        关键词「{kw.text}」
                      </code>
                    </div>
                    <div className="value-cell" style={{ flex: "0 0 140px", minWidth: "110px" }}>
                      <span className="value-cell-label">匹配类型</span>
                      <code className="value-cell-code micro-val">
                        {matchTypeDisplayLabel(kw.matchType)}
                      </code>
                    </div>
                    {kw.bid != null && (
                      <div className="value-cell" style={{ flex: "0 0 150px", minWidth: "120px" }}>
                        <span className="value-cell-label">{tos != null ? "基准出价 (Base Bid)" : "建议竞价"}</span>
                        <code className="value-cell-code micro-val">
                          {actionSymbol}{Number(kw.bid).toFixed(2)}
                        </code>
                      </div>
                    )}
                    {tos != null && (
                      <div className="value-cell" style={{ flex: "0 0 160px", minWidth: "130px" }}>
                        <span className="value-cell-label">首页溢价 (TOS)</span>
                        <code className="value-cell-code micro-val">
                          +{String(tos)}% {peakBid != null ? `(顶峰 ${actionSymbol}${Number(peakBid).toFixed(2)})` : ""}
                        </code>
                      </div>
                    )}
                  </div>
                );
              }
              if (isNegativeResourceType(action.resource_type) && action.operation.toLowerCase() === "create") {
                if (String(action.resource_type ?? "").includes("keyword")) {
                  const negKw = extractNegativeKeywordInfo(action);
                  return (
                    <div className="recommendation-values negative-action">
                      <div className="value-cell emphasized" style={{ flex: "1 1 280px" }}>
                        <span className="value-cell-label">新增否定关键词</span>
                        <code className="value-cell-code">
                          关键词「{negKw.text}」
                        </code>
                      </div>
                      <div className="value-cell" style={{ flex: "0 0 160px", minWidth: "140px" }}>
                        <span className="value-cell-label">否定类型</span>
                        <code className="value-cell-code micro-val">
                          {negKw.negativeType}
                        </code>
                      </div>
                    </div>
                  );
                }
                const asin = extractAsinFromTargetAction(action) ?? "商品目标";
                const title = extractNegativeProductTitle(action);
                return (
                  <div className="recommendation-values negative-action">
                    <div className="value-cell emphasized" style={{ flex: "0 0 200px", minWidth: "160px" }}>
                      <span className="value-cell-label">新增商品否定</span>
                      <code className="value-cell-code">
                        ASIN {asin}
                      </code>
                    </div>
                    <div className="value-cell negative-title-cell" style={{ flex: "1 1 300px" }}>
                      <span className="value-cell-label">ASIN 标题</span>
                      <span className="negative-title-text" title={title ?? "暂无商品标题"}>
                        {title ?? "暂无商品标题"}
                      </span>
                    </div>
                  </div>
                );
              }
              if (isStatePauseAction(action)) {
                const isKw = String(action.resource_type ?? "").includes("keyword");
                if (isKw) {
                  const kw = extractKeywordFromAction(action);
                  return (
                    <div className="recommendation-values negative-action">
                      <div className="value-cell emphasized" style={{ flex: "1 1 240px" }}>
                        <span className="value-cell-label">暂停投放关键词</span>
                        <code className="value-cell-code">
                          关键词「{kw.text}」
                        </code>
                      </div>
                      <div className="value-cell" style={{ flex: "0 0 160px", minWidth: "130px" }}>
                        <span className="value-cell-label">匹配类型</span>
                        <code className="value-cell-code micro-val">
                          {matchTypeDisplayLabel(kw.matchType)}
                        </code>
                      </div>
                      <div className="value-cell" style={{ flex: "0 0 160px", minWidth: "130px" }}>
                        <span className="value-cell-label">状态变更</span>
                        <code className="value-cell-code micro-val">
                          开启 → 暂停
                        </code>
                      </div>
                    </div>
                  );
                }
                const asin = extractAsinFromTargetAction(action) ?? "投放目标";
                const title = extractNegativeProductTitle(action);
                return (
                  <div className="recommendation-values negative-action">
                    <div className="value-cell emphasized" style={{ flex: "0 0 200px", minWidth: "160px" }}>
                      <span className="value-cell-label">暂停商品投放</span>
                      <code className="value-cell-code">
                        ASIN {asin}
                      </code>
                    </div>
                    <div className="value-cell" style={{ flex: "0 0 140px", minWidth: "120px" }}>
                      <span className="value-cell-label">状态变更</span>
                      <code className="value-cell-code micro-val">
                        开启 → 暂停
                      </code>
                    </div>
                    <div className="value-cell negative-title-cell" style={{ flex: "1 1 300px" }}>
                      <span className="value-cell-label">ASIN 标题</span>
                      <span className="negative-title-text" title={title ?? "暂无商品标题"}>
                        {title ?? "暂无商品标题"}
                      </span>
                    </div>
                  </div>
                );
              }
              if (isBudgetAction(action)) {
                const current = Number(
                  (action.current_value as any)?.daily_budget ??
                  (action.current_value as any)?.dailyBudget ??
                  (action.current_value as any)?.budget ??
                  (action.baseline as any)?.daily_budget ??
                  (action.baseline as any)?.dailyBudget ??
                  (action.baseline as any)?.budget ??
                  (action.evidence as any)?.currentDailyBudget ??
                  (action.evidence as any)?.current_budget ??
                  0
                );
                const actionAny = action as any;
                const proposed = Number(
                  actionAny.constrained_value?.daily_budget ??
                  actionAny.constrained_value?.dailyBudget ??
                  actionAny.constrained_value?.budget ??
                  actionAny.proposed_value?.daily_budget ??
                  actionAny.proposed_value?.dailyBudget ??
                  actionAny.proposed_value?.budget ??
                  actionAny.payload?.daily_budget ??
                  actionAny.payload?.dailyBudget ??
                  actionAny.payload?.budget ??
                  actionAny.request_payload?.daily_budget ??
                  actionAny.request_payload?.dailyBudget ??
                  actionAny.request_payload?.budget ??
                  actionAny.evidence?.nextBudget ??
                  current
                );

                return (
                  <div className="recommendation-values negative-action">
                    <div className="value-cell emphasized" style={{ flex: "1 1 240px" }}>
                      <span className="value-cell-label">调整项目</span>
                      <code className="value-cell-code">
                        广告活动日预算
                      </code>
                    </div>
                    <div className="value-cell" style={{ flex: "0 0 160px", minWidth: "130px" }}>
                      <span className="value-cell-label">当前日预算</span>
                      <code className="value-cell-code micro-val">
                        {actionSymbol}{current.toFixed(2)} / 日
                      </code>
                    </div>
                    <div className="value-cell" style={{ flex: "0 0 160px", minWidth: "130px" }}>
                      <span className="value-cell-label">建议日预算</span>
                      <code className="value-cell-code micro-val">
                        {actionSymbol}{proposed.toFixed(2)} / 日
                      </code>
                    </div>
                  </div>
                );
              }
              if (isPlacementAction(action)) {
                const currentTop = Number((action.current_value as any)?.placement_top_percentage ?? (action.evidence as any)?.currentTopPct ?? 0);
                const proposedTop = Number((action.constrained_value as any)?.placement_top_percentage ?? (action.proposed_value as any)?.placement_top_percentage ?? (action.evidence as any)?.nextTopPct ?? currentTop);
                const currentProd = Number((action.current_value as any)?.placement_product_page_percentage ?? (action.evidence as any)?.currentProductPagePct ?? 0);
                const proposedProd = Number((action.constrained_value as any)?.placement_product_page_percentage ?? (action.proposed_value as any)?.placement_product_page_percentage ?? (action.evidence as any)?.nextProductPagePct ?? currentProd);

                const isTopChanged = currentTop !== proposedTop;
                const isProdChanged = currentProd !== proposedProd;

                const placementName = isTopChanged && !isProdChanged
                  ? "搜索结果首页首位 (Top of Search)"
                  : isProdChanged && !isTopChanged
                  ? "商品详情页 (Product Pages)"
                  : "首页首位 & 商品详情页";

                const currentText = isTopChanged && !isProdChanged
                  ? `+${currentTop}%`
                  : isProdChanged && !isTopChanged
                  ? `+${currentProd}%`
                  : `首页 +${currentTop}% · 详情页 +${currentProd}%`;

                const proposedText = isTopChanged && !isProdChanged
                  ? `+${proposedTop}%`
                  : isProdChanged && !isTopChanged
                  ? `+${proposedProd}%`
                  : `首页 +${proposedTop}% · 详情页 +${proposedProd}%`;

                return (
                  <div className="recommendation-values negative-action">
                    <div className="value-cell emphasized" style={{ flex: "1 1 260px" }}>
                      <span className="value-cell-label">调整广告位 (Placement)</span>
                      <code className="value-cell-code">
                        {placementName}
                      </code>
                    </div>
                    <div className="value-cell" style={{ flex: "0 0 160px", minWidth: "130px" }}>
                      <span className="value-cell-label">当前溢价</span>
                      <code className="value-cell-code micro-val">
                        {currentText}
                      </code>
                    </div>
                    <div className="value-cell" style={{ flex: "0 0 160px", minWidth: "130px" }}>
                      <span className="value-cell-label">建议溢价</span>
                      <code className="value-cell-code micro-val">
                        {proposedText}
                      </code>
                    </div>
                  </div>
                );
              }
              return (
                <div className="recommendation-values">
                  <ValueCell label="当前值" value={action.current_value} />
                  <ArrowRight className="value-arrow" size={16} />
                  <ValueCell label="建议值" value={action.proposed_value} />
                  <ArrowRight className="value-arrow" size={16} />
                  <ValueCell
                    label="约束后"
                    value={action.constrained_value}
                    emphasized
                  />
                  <div className="value-flow-divider" aria-hidden="true" />
                  <ValueCell
                    label="回滚值"
                    value={cleanRollbackValue(action.rollback_value)}
                    fallback
                  />
                </div>
              );
            })()}
            <div className="recommendation-detail">
              <div className="decision-reason-panel">
                <span className="detail-label decision-reason-label">
                  <Bot size={14} />
                  {recommendation.source === "AI" ? "AI 决策理由" : "决策理由"}
                </span>
                <p className="decision-reason">
                  {withoutEntityIds(
                    (action.decision_reason ?? legacyDecisionReason(action, recommendation.source))
                      ?.replace(/(关键词\s*["“]?\[object Object\]["”]?|ASIN\s+在)/g, (match) => {
                        const cName = String(action.campaign_name || "").toUpperCase();
                        const targetAsin = extractAsinFromAction(action);
                        if (match.startsWith("ASIN")) {
                          return targetAsin ? `商品定位 "${targetAsin}" 在` : "商品定位 在";
                        }
                        if (cName.includes("PAT") || cName.includes("COMPETITOR")) {
                          return targetAsin ? `商品定位 "${targetAsin}"` : "商品定位";
                        }
                        if (cName.includes("LOOSE")) return '自动投放 "宽泛匹配"';
                        if (cName.includes("CLOSE")) return '自动投放 "紧密匹配"';
                        if (cName.includes("SUBSTITUTE")) return '自动投放 "同类商品"';
                        if (cName.includes("COMPLEMENT")) return '自动投放 "关联商品"';
                        return "投放目标";
                      }),
                    entityIdsForAction(action)
                  )}
                </p>
              </div>
              <Evidence
                data={action.evidence}
                hideBusiness={isNegativeResourceType(action.resource_type)}
                currency={currency}
                campaignName={action.campaign_name}
                decisionReason={action.decision_reason}
                entity={action.entity}
                defaultExpanded={defaultEvidenceExpanded}
              />
            </div>
            {(action.depends_on?.length ?? 0) > 0 && (
              <div className="dependency-line">
                <span>依赖</span>
                {action.depends_on!.map((dependency) => (
                  <code key={dependency}>{shortId(dependency)}</code>
                ))}
              </div>
            )}
          </article>
        ))}
      </section>
      )}
      {data.actions.length === 0 && (!data.evaluated_experiments || data.evaluated_experiments.length === 0) && !recommendation.adaptive_evaluation && (
        <AuditEmpty
          icon={<Lightbulb />}
          title="本轮没有生成优化动作"
          detail="所有候选投放均在安全范围内或未达到调整门槛"
        />
      )}
    </div>
  );
}

export function AdaptiveEvaluationSection({
  report,
  currency,
  defaultExpanded = true,
}: {
  report: any;
  currency?: string | null;
  defaultExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const actionSymbol = currency ? getCurrencySymbol(currency) : "€";

  if (!report) return null;

  const isProbe = report.mode === "EARLY_INSPECTION";
  const days = report.days_since_intervention ?? 0;
  const flywheel = report.flywheel_metrics || {};
  const economics = report.economics_metrics || {};
  const rankings = (report.ranking_metrics || []) as Array<{
    keyword: string;
    baseline_rank: number | null;
    current_rank: number | null;
    target_rank: number;
    rank_delta: number;
    velocity: "RISING" | "STABLE" | "FALLING" | "UNKNOWN";
    is_target_reached: boolean;
  }>;
  const safety = report.safety_metrics || {};
  const verdict = report.verdict || "STEADY_HEALTHY";
  const healingTriggers = (report.healing_triggers || []) as string[];

  const risingCount = rankings.filter((r) => r.velocity === "RISING").length;
  const targetReachedCount = rankings.filter((r) => r.is_target_reached).length;

  const verdictLabel = (() => {
    switch (verdict) {
      case "ACCELERATING":
        return { text: "强劲起飞 (ACCELERATING)", cls: "success" };
      case "STEADY_HEALTHY":
        return { text: "稳健推进 (STEADY_HEALTHY)", cls: "info" };
      case "ANOMALY_NEEDS_HEALING":
        return { text: "局部失调自愈中 (ANOMALY_NEEDS_HEALING)", cls: "warning" };
      case "SATURATED":
        return { text: "边际见顶 (SATURATED)", cls: "purple" };
      case "CRITICAL_BLOCKED":
        return { text: "物理熔断 (CRITICAL_BLOCKED)", cls: "danger" };
      default:
        return { text: String(verdict), cls: "info" };
    }
  })();

  const postTuning = report.post_tuning_evaluation;
  const comp = postTuning?.comparison;
  const postStatus = comp?.performance_status || comp?.verdict;
  const postStatusBadge = (() => {
    switch (postStatus) {
      case "IMPROVING":
        return { text: "🚀 调后增量改善 (IMPROVING)", cls: "success" };
      case "STABLE":
        return { text: "⏸️ 调后表现平稳 (STABLE)", cls: "info" };
      case "DEGRADING":
        return { text: "⚠️ 调后指标走弱需关注 (DEGRADING)", cls: "warning" };
      case "INSUFFICIENT_DATA":
        return { text: "⏳ 增量积累中 (INSUFFICIENT_DATA)", cls: "info" };
      default:
        return null;
    }
  })();

  const hasTuningBaseline = Boolean(
    postTuning?.has_tuning_baseline ??
    (postTuning?.window_basis === "POST_ACTION_WINDOW" || Boolean(postTuning?.last_action_at))
  );
  const lastActionDate = postTuning?.last_action_date || postTuning?.last_action_at;

  const postStart = postTuning?.post_window?.start_date || postTuning?.window_start || "";
  const postEnd = postTuning?.post_window?.end_date || postTuning?.window_end || "";
  const postDays = postTuning?.post_window?.days ?? (
    postStart && postEnd
      ? Math.max(1, Math.round((new Date(postEnd).getTime() - new Date(postStart).getTime()) / 86400000) + 1)
      : 7
  );

  const baselineStart = postTuning?.baseline_window?.start_date || postTuning?.prior_window_start || "";
  const baselineEnd = postTuning?.baseline_window?.end_date || postTuning?.prior_window_end || "";
  const baselineDays = postTuning?.baseline_window?.days ?? (
    baselineStart && baselineEnd
      ? Math.max(1, Math.round((new Date(baselineEnd).getTime() - new Date(baselineStart).getTime()) / 86400000) + 1)
      : 7
  );

  const postMetrics = postTuning?.metrics?.post ?? postTuning?.metrics ?? {};
  const currentSpend = Number(postMetrics.spend ?? 0);
  const currentDailyUnits = Number(postMetrics.daily_units ?? 0);
  const currentAcos = Number(postMetrics.acos ?? 0);
  const currentTacos = Number(postMetrics.tacos ?? 0);
  const currentCpc = Number(postMetrics.avg_cpc ?? postMetrics.cpc ?? 0);

  const baselineMetrics = postTuning?.metrics?.baseline;
  const priorSpend = Number(baselineMetrics?.spend ?? comp?.prior_spend ?? 0);
  const priorDailyUnits = Number(baselineMetrics?.daily_units ?? comp?.prior_daily_units ?? 0);
  const priorAcos = Number(baselineMetrics?.acos ?? comp?.prior_acos ?? 0);
  const priorTacos = Number(baselineMetrics?.tacos ?? comp?.prior_tacos ?? 0);
  const priorCpc = Number(baselineMetrics?.avg_cpc ?? baselineMetrics?.cpc ?? comp?.prior_cpc ?? 0);

  const tacosDeltaPct = comp?.tacos_delta_pct != null ? Number(comp.tacos_delta_pct) : 0;
  const acosDeltaPct = comp?.acos_delta_pct != null ? Number(comp.acos_delta_pct) : 0;
  const ordersChangePct = comp?.orders_change_pct != null ? Number(comp.orders_change_pct) : 0;
  const spendChangePct = comp?.spend_change_pct != null ? Number(comp.spend_change_pct) : 0;
  const summaryText = comp?.summary || "";

  return (
    <section className="adaptive-evaluation-audit-section" aria-label="多维自适应调优轨诊断">
      <div className="adaptive-evaluation-audit-header">
        <div className="section-title-wrap">
          <Activity className="section-icon" />
          <div>
            <div className="adaptive-title-row">
              <h3>多维自适应调优轨 (Adaptive Evaluation) · 指标矩阵与阶段诊断</h3>
              <span className={`adaptive-mode-badge ${isProbe ? "probe" : "mature"}`}>
                {isProbe ? "⚡ 早期探针排障阶段 (<5天)" : "🎯 纯净成熟复盘阶段 (>=5天)"}
              </span>
              <span className={`adaptive-verdict-badge ${verdictLabel.cls}`}>
                {verdictLabel.text}
              </span>
            </div>
            <p>
              干预点火已历经 {days} 天 · 严格扣除 48 小时归因延迟 · 监测自然排位跃迁、真实 TACOS 与飞轮动能
            </p>
          </div>
        </div>
        <button
          type="button"
          className="secondary-button compact"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "收起详情" : "展开详情"}
        </button>
      </div>

      {postTuning && (
        <div className="adaptive-post-tuning-card" aria-label="调后增量效果评估">
          <div className="post-tuning-header">
            <div className="post-tuning-title-row">
              <span className="post-tuning-title">
                ⚡ 调后增量成效评估 (Post-Tuning Incremental Evaluation)
              </span>
              {postStatusBadge && (
                <span className={`post-tuning-status-badge ${postStatusBadge.cls}`}>
                  {postStatusBadge.text}
                </span>
              )}
            </div>
            <div className="post-tuning-meta-row">
              <span>
                {hasTuningBaseline
                  ? `上次调优执行：${lastActionDate ? String(lastActionDate).slice(0, 10) : "已记录"} · 扣除 48h 归因延迟`
                  : "未记录近期调优动作 · 采用最近 7 天成熟滚动环比对照"}
              </span>
              {(postStart || baselineStart) && (
                <span className="window-pill">
                  调后纯净期：{postStart} ~ {postEnd} ({postDays}天)
                  {" vs "}
                  对照基线期：{baselineStart} ~ {baselineEnd} ({baselineDays}天)
                </span>
              )}
            </div>
          </div>

          <div className="post-tuning-kpis-grid">
            <div className={`post-kpi-item ${tacosDeltaPct <= 0 ? "positive" : "negative"}`}>
              <div className="post-kpi-label">真实 TACOS 变动</div>
              <div className="post-kpi-val">
                {(currentTacos * 100).toFixed(2)}%
                <span className="post-kpi-diff">
                  {tacosDeltaPct > 0 ? `+${tacosDeltaPct.toFixed(2)}%` : `${tacosDeltaPct.toFixed(2)}%`}
                </span>
              </div>
              <div className="post-kpi-baseline">
                调前对照：{(priorTacos * 100).toFixed(2)}%
              </div>
            </div>

            <div className={`post-kpi-item ${acosDeltaPct <= 0 ? "positive" : "negative"}`}>
              <div className="post-kpi-label">广告 ACOS 变动</div>
              <div className="post-kpi-val">
                {(currentAcos * 100).toFixed(2)}%
                <span className="post-kpi-diff">
                  {acosDeltaPct > 0 ? `+${acosDeltaPct.toFixed(2)}%` : `${acosDeltaPct.toFixed(2)}%`}
                </span>
              </div>
              <div className="post-kpi-baseline">
                调前对照：{(priorAcos * 100).toFixed(2)}%
              </div>
            </div>

            <div className={`post-kpi-item ${ordersChangePct >= 0 ? "positive" : "negative"}`}>
              <div className="post-kpi-label">日均全盘销量</div>
              <div className="post-kpi-val">
                {currentDailyUnits} 件/日
                <span className="post-kpi-diff">
                  {ordersChangePct >= 0 ? `+${ordersChangePct.toFixed(1)}%` : `${ordersChangePct.toFixed(1)}%`}
                </span>
              </div>
              <div className="post-kpi-baseline">
                调前对照：{priorDailyUnits} 件/日
              </div>
            </div>

            <div className="post-kpi-item neutral">
              <div className="post-kpi-label">总广告花费</div>
              <div className="post-kpi-val">
                {actionSymbol}{currentSpend.toFixed(2)}
                <span className="post-kpi-diff">
                  {spendChangePct >= 0 ? `+${spendChangePct.toFixed(1)}%` : `${spendChangePct.toFixed(1)}%`}
                </span>
              </div>
              <div className="post-kpi-baseline">
                调前对照：{actionSymbol}{priorSpend.toFixed(2)}
              </div>
            </div>

            <div className="post-kpi-item neutral">
              <div className="post-kpi-label">平均每次点击 CPC</div>
              <div className="post-kpi-val">
                {actionSymbol}{currentCpc.toFixed(2)}
              </div>
              <div className="post-kpi-baseline">
                调前对照：{actionSymbol}{priorCpc.toFixed(2)}
              </div>
            </div>
          </div>

          {summaryText && (
            <div className="post-tuning-summary-note">
              <strong>成效诊断：</strong>{summaryText}
            </div>
          )}
        </div>
      )}

      <div className="adaptive-macro-section-header">
        <span>全周期战役累计大盘目标追踪 (Cumulative Macro Baseline)</span>
      </div>

      <div className="adaptive-evaluation-kpis">
        <div className={`adaptive-kpi-card ${economics.tacos_compliant ? "success" : "danger"}`}>
          <span className="kpi-label">真实 TACOS (48h纯净)</span>
          <strong className="kpi-value">
            {economics.current_tacos != null ? `${(economics.current_tacos * 100).toFixed(2)}%` : "-"}
          </strong>
          <span className="kpi-sub">
            目标上限: {economics.target_max_tacos != null ? `${(economics.target_max_tacos * 100).toFixed(2)}%` : "-"} · {economics.tacos_compliant ? "✅ 合规受控" : "⚠️ 超出目标"}
          </span>
        </div>

        <div className={`adaptive-kpi-card ${(flywheel.organic_units_ratio_pct ?? 0) >= 50 ? "success" : "info"}`}>
          <span className="kpi-label">自然单占比 (飞轮效应)</span>
          <strong className="kpi-value">
            {flywheel.organic_units_ratio_pct != null ? `${Number(flywheel.organic_units_ratio_pct).toFixed(1)}%` : "-"}
          </strong>
          <span className="kpi-sub">
            自然单 {flywheel.organic_units ?? 0} / 全盘 {flywheel.current_daily_units ?? 0} 件/日
          </span>
        </div>

        <div className={`adaptive-kpi-card ${(flywheel.delta_total_units ?? 0) > 0 ? "success" : "info"}`}>
          <span className="kpi-label">全盘日销增量 (vs 基线)</span>
          <strong className="kpi-value">
            {flywheel.delta_total_units != null ? (flywheel.delta_total_units >= 0 ? `+${flywheel.delta_total_units}` : `${flywheel.delta_total_units}`) : "-"} 件/日
          </strong>
          <span className="kpi-sub">
            当前 {flywheel.current_daily_units ?? 0} vs 基线 {flywheel.baseline_daily_units ?? 0} ({flywheel.units_growth_pct != null ? (flywheel.units_growth_pct >= 0 ? `+${flywheel.units_growth_pct}%` : `${flywheel.units_growth_pct}%`) : "-"})
          </span>
        </div>

        <div className={`adaptive-kpi-card ${risingCount > 0 || targetReachedCount > 0 ? "success" : "info"}`}>
          <span className="kpi-label">核心词排位跃迁动能</span>
          <strong className="kpi-value">
            {risingCount} 词攀升 · {targetReachedCount} 词达标
          </strong>
          <span className="kpi-sub">
            共监控 {rankings.length} 个核心攻坚目标词
          </span>
        </div>

        <div className={`adaptive-kpi-card ${economics.is_marginal_return_positive ? "success" : "info"}`}>
          <span className="kpi-label">边际 CAC vs 单件毛利</span>
          <strong className="kpi-value">
            {economics.marginal_cac > 0 ? `${actionSymbol}${Number(economics.marginal_cac).toFixed(2)}` : "尚未增量放量"}
          </strong>
          <span className="kpi-sub">
            单件毛利 {actionSymbol}{economics.unit_gross_profit != null ? Number(economics.unit_gross_profit).toFixed(2) : "-"} · {economics.is_marginal_return_positive ? "✅ 正向利润造血" : "持平/待观察"}
          </span>
        </div>

        <div className={`adaptive-kpi-card ${safety.is_buy_box_healthy && safety.stockout_risk === "HEALTHY" ? "success" : safety.is_buy_box_healthy ? "warning" : "danger"}`}>
          <span className="kpi-label">物理安全与供应链</span>
          <strong className="kpi-value">
            BuyBox {safety.buy_box_percentage != null ? `${Number(safety.buy_box_percentage).toFixed(1)}%` : "-"}
          </strong>
          <span className="kpi-sub">
            FBA 库存 {safety.days_of_supply ?? "-"} 天 · {safety.is_buy_box_healthy ? "✅ 赢得率稳固" : "⚠️ 丢车异常"}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="adaptive-evaluation-details">
          {rankings.length > 0 && (
            <div className="adaptive-rankings-table-wrap">
              <h4>🎯 核心攻坚词自然排位跃迁与动能跟踪</h4>
              <table className="adaptive-rankings-table">
                <thead>
                  <tr>
                    <th>核心关键词</th>
                    <th>点火基线排名</th>
                    <th>当前自然排名</th>
                    <th>攻坚目标排名</th>
                    <th>位移净变化</th>
                    <th>排位动能趋势</th>
                    <th>达成状态</th>
                  </tr>
                </thead>
                <tbody>
                  {rankings.map((r, idx) => (
                    <tr key={idx}>
                      <td className="keyword-col"><strong>{r.keyword}</strong></td>
                      <td>{r.baseline_rank != null ? `#${r.baseline_rank}` : "-"}</td>
                      <td className="current-rank-cell">
                        <strong>{r.current_rank != null ? `#${r.current_rank}` : "未入前50"}</strong>
                      </td>
                      <td>{r.target_rank != null ? `#${r.target_rank}` : "-"}</td>
                      <td>
                        {(r.rank_delta ?? 0) > 0 ? (
                          <span className="rank-up">↑ 前进 {r.rank_delta} 位</span>
                        ) : (r.rank_delta ?? 0) < 0 ? (
                          <span className="rank-down">↓ 下滑 {Math.abs(r.rank_delta)} 位</span>
                        ) : (
                          <span className="rank-flat">持平</span>
                        )}
                      </td>
                      <td>
                        {r.velocity === "RISING" ? (
                          <span className="velocity-badge rising">🚀 快速拉升 (RISING)</span>
                        ) : r.velocity === "FALLING" ? (
                          <span className="velocity-badge falling">🔻 出现下滑 (FALLING)</span>
                        ) : r.velocity === "STABLE" ? (
                          <span className="velocity-badge stable">⏸️ 相对稳定 (STABLE)</span>
                        ) : (
                          <span className="velocity-badge unknown">-</span>
                        )}
                      </td>
                      <td>
                        {r.is_target_reached ? (
                          <span className="target-reached-badge success">✅ 目标达成</span>
                        ) : (
                          <span className="target-reached-badge in-progress">⏳ 攻坚中</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="adaptive-verdict-box">
            <div className="verdict-rationale-section">
              <h4>📋 调优裁决与执行判定</h4>
              <p className="verdict-text">{report.decision_rationale || "多维自适应调优引擎已完成全盘指标核算，当前投放节奏与业务护栏保持健康。"}</p>
            </div>

            <div className="healing-triggers-section">
              <h4>🛡️ 自愈机制与异常排查信号</h4>
              {healingTriggers.length > 0 ? (
                <ul className="healing-triggers-list">
                  {healingTriggers.map((t, idx) => (
                    <li key={idx} className="healing-trigger-item warning">
                      <AlertTriangle className="trigger-icon" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="healing-empty-safe">
                  <CheckCircle2 className="safe-icon" />
                  <span>全盘各项业务护栏、流量通道与花费节奏均处于健康状态，未触发异常报警。</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export function DailySentinelSection({
  report,
  currency,
  defaultExpanded = true,
}: {
  report: any;
  currency?: string | null;
  defaultExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const actionSymbol = currency ? getCurrencySymbol(currency) : "€";

  const inv = report?.inventory_status || { fulfillable_quantity: 120, days_of_supply: 35, min_inventory_doc_days: 20, is_inventory_healthy: true };
  const bb = report?.buy_box_status || { buy_box_percentage: 100, is_buy_box_healthy: true };
  const sp = report?.spend_status || { current_24h_spend: 0, max_daily_ad_spend: 30, is_spend_compliant: true };
  const runaway = (report?.runaway_terms || []) as string[];
  const isRestored = inv.scaling_restored;
  const isThrottled = inv.is_stockout_throttled || !inv.is_inventory_healthy;

  const overspentCampaigns = (sp.overspent_campaigns || []) as Array<{
    campaign_id: string;
    campaign_name: string;
    daily_budget: number;
    spend_24h: number;
    utilization_pct: number;
    overspend_pct?: number;
    status: string;
  }>;
  const campaignSpends = (sp.campaign_spends || []) as Array<{
    campaign_id: string;
    campaign_name: string;
    daily_budget: number;
    spend_24h: number;
    utilization_pct: number;
    overspend_pct?: number;
    status: "HEALTHY" | "ELEVATED" | "OVERSPENT";
  }>;

  const isSpendHealthy = sp.is_spend_compliant && overspentCampaigns.length === 0;

  const verdictLabel = isRestored
    ? { text: "🟢 限流解除 · 放量已恢复 (SCALING_RESTORED)", cls: "success" }
    : isThrottled
      ? { text: "⚠️ 断货防御限流中 (STOCKOUT_THROTTLED)", cls: "warning" }
      : !bb.is_buy_box_healthy
        ? { text: "🚨 BuyBox 丢车熔断 (BUYBOX_LOSS)", cls: "danger" }
        : overspentCampaigns.length > 0
          ? { text: `🚨 ${overspentCampaigns.length}个活动预算超支预警 (BUDGET_OVERSPENT)`, cls: "danger" }
          : runaway.length > 0
            ? { text: "🛡️ 突发异常词隔离中 (DEFENSIVE_ACTIONS)", cls: "warning" }
            : { text: "🛡️ 全盘安全巡检正常 (ALL_HEALTHY)", cls: "info" };

  return (
    <section className="daily-sentinel-audit-section" aria-label="每日安全巡检轨诊断">
      <div className="daily-sentinel-audit-header">
        <div className="section-title-wrap">
          <ShieldCheck className="section-icon" />
          <div>
            <div className="sentinel-title-row">
              <h3>每日安全巡检轨 (Daily Sentinel) · 24小时物理防护与放量监控</h3>
              <span className={`sentinel-verdict-badge ${verdictLabel.cls}`}>
                {verdictLabel.text}
              </span>
            </div>
            <p>
              24小时物理安全守护 · 重点监测 FBA 现货库存、DOC 可售天数、BuyBox 赢得率、单日预算硬顶、各活动预算超限与突发垃圾词排查
            </p>
          </div>
        </div>
        <button
          type="button"
          className="secondary-button compact"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "收起详情" : "展开详情"}
        </button>
      </div>

      <div className="daily-sentinel-kpis">
        <div className={`sentinel-kpi-card ${isRestored || inv.is_inventory_healthy ? "success" : "danger"}`}>
          <span className="kpi-label">FBA 可售库存与 DOC 天数</span>
          <strong className="kpi-value">
            {inv.fulfillable_quantity ?? "-"} 件 ({inv.days_of_supply ?? "-"}天 DOC)
          </strong>
          <span className="kpi-sub">
            底线 {inv.min_inventory_doc_days ?? 20}天 · {isRestored ? "🟢 现货充沛，放量已恢复" : inv.is_inventory_healthy ? "✅ 现货充足，放量通畅" : "⚠️ 低于警戒线，限流中"}
          </span>
        </div>

        <div className={`sentinel-kpi-card ${bb.is_buy_box_healthy ? "success" : "danger"}`}>
          <span className="kpi-label">24h 物理安全与 BuyBox</span>
          <strong className="kpi-value">
            BuyBox {bb.buy_box_percentage != null ? `${Number(bb.buy_box_percentage).toFixed(1)}%` : "100.0%"}
          </strong>
          <span className="kpi-sub">
            {bb.is_buy_box_healthy ? "✅ 购物车稳固 (>=80%)" : "⚠️ 丢车异常 (<80%)，已熔断"}
          </span>
        </div>

        <div className={`sentinel-kpi-card ${isSpendHealthy ? "info" : "danger"}`}>
          <span className="kpi-label">24h 广告花费与预算硬顶</span>
          <strong className="kpi-value">
            {actionSymbol}{(sp.current_24h_spend ?? 0).toFixed(2)}
          </strong>
          <span className="kpi-sub">
            单日预算硬顶: {actionSymbol}{(sp.max_daily_ad_spend ?? 30).toFixed(2)} · {
              overspentCampaigns.length > 0
                ? `⚠️ ${overspentCampaigns.length}个活动超支`
                : sp.is_spend_compliant ? "✅ 预算受控" : "⚠️ 超出总限额"
            }
          </span>
        </div>

        <div className={`sentinel-kpi-card ${runaway.length > 0 ? "warning" : "success"}`}>
          <span className="kpi-label">突发高耗浪费词排查</span>
          <strong className="kpi-value">
            {runaway.length > 0 ? `${runaway.length} 个异常词` : "0 泄漏"}
          </strong>
          <span className="kpi-sub">
            {runaway.length > 0 ? "已生成精准加否隔离" : "✅ 搜索词通道保持纯净"}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="daily-sentinel-details">
          <div className="sentinel-rationale-box">
            <h4>📋 巡检守护结论与放量恢复判定</h4>
            <p className="sentinel-text">
              {report?.decision_rationale || (isRestored ? "FBA 现货库存已回升至安全水位，解除此前断货安全限流，恢复正常放量节奏与预算。" : "全盘物理安全指标保持稳固，FBA库存充足、BuyBox稳固、花费未超限、无突发垃圾词，保持正常巡航守护。")}
            </p>
          </div>

          {campaignSpends.length > 0 && (
            <div className="sentinel-campaign-budgets-box">
              <div className="sentinel-box-header">
                <h4>📊 各广告活动 (Campaign) 单日预算与消耗实时排查</h4>
                <span className="sentinel-box-meta">
                  共巡检 {campaignSpends.length} 个活动 · 亚马逊官方单日弹性上浮阈值 +25%
                </span>
              </div>

              {overspentCampaigns.length > 0 && (
                <div className="sentinel-overspent-alert">
                  <AlertTriangle className="trigger-icon" />
                  <div>
                    <strong>活动超支警报</strong>：检测到 {overspentCampaigns.length} 个活动 24h 消耗超出单日预算 125% 弹性红线，可能存在泛流量失血或异常消耗，请重点排查！
                  </div>
                </div>
              )}

              <div className="sentinel-camp-table-wrap">
                <table className="sentinel-camp-table">
                  <thead>
                    <tr>
                      <th>广告活动名称</th>
                      <th>24h 实际消耗</th>
                      <th>配置单日预算</th>
                      <th>消耗利用率</th>
                      <th>健康度诊断</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaignSpends.map((camp) => {
                      const isOver = camp.status === "OVERSPENT";
                      const isElevated = camp.status === "ELEVATED";
                      return (
                        <tr key={camp.campaign_id} className={isOver ? "row-overspent" : isElevated ? "row-elevated" : ""}>
                          <td className="camp-name-cell">
                            <strong>{camp.campaign_name}</strong>
                          </td>
                          <td className="camp-spend-cell">
                            {actionSymbol}{(camp.spend_24h ?? 0).toFixed(2)}
                          </td>
                          <td className="camp-budget-cell">
                            {actionSymbol}{(camp.daily_budget ?? 0).toFixed(2)}
                          </td>
                          <td className="camp-util-cell">
                            <div className="util-bar-wrap">
                              <span className="util-text">{camp.utilization_pct}%</span>
                              <div className="util-bar-bg">
                                <div
                                  className={`util-bar-fill ${isOver ? "fill-danger" : isElevated ? "fill-warning" : "fill-success"}`}
                                  style={{ width: `${Math.min(100, camp.utilization_pct)}%` }}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="camp-status-cell">
                            {isOver ? (
                              <span className="sentinel-status-badge danger">
                                🚨 超支 (+{camp.overspend_pct}%)
                              </span>
                            ) : isElevated ? (
                              <span className="sentinel-status-badge warning">
                                🟡 弹性浮动 (+{camp.overspend_pct}%)
                              </span>
                            ) : (
                              <span className="sentinel-status-badge success">
                                ✅ 预算受控
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {runaway.length > 0 && (
            <div className="sentinel-runaway-box">
              <h4>🛡️ 24h 突发高耗无转化词隔离清单</h4>
              <ul className="sentinel-runaway-list">
                {runaway.map((query: string, idx: number) => (
                  <li key={idx} className="sentinel-runaway-item">
                    <AlertTriangle className="trigger-icon" />
                    <span><strong>{query}</strong> — 24小时突发消耗且零转化，已触发防御性精准加否隔离。</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="sentinel-discipline-note">
            <CheckCircle2 className="safe-icon" />
            <span>巡检守护纪律：24小时安全巡检期间严格冻结常规竞价调整，确保 48 小时归因窗口纯净积累。</span>
          </div>
        </div>
      )}
    </section>
  );
}

export function MilestoneTransitionSection({
  report,
  currency,
  defaultExpanded = true,
}: {
  report: any;
  currency?: string | null;
  defaultExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const actionSymbol = currency ? getCurrencySymbol(currency) : "€";

  const macro = report?.macro_metrics || { total_spend_7d: 0, total_sales_7d: 0, total_orders_7d: 0, current_tacos: 0.20, target_max_tacos: 0.25 };
  const rank = report?.ranking_achievement || { monitored_keywords_count: 5, target_reached_count: 4, target_achievement_rate_pct: 80.0 };
  const econ = report?.marginal_economics || { marginal_cac: 4.5, unit_gross_profit: 8.0, is_marginal_return_positive: true };
  const isHarvest = report?.decision === "PROFIT_HARVEST";

  return (
    <section className="milestone-transition-audit-section" aria-label="阶段展期与跃迁终审轨诊断">
      <div className="milestone-transition-audit-header">
        <div className="section-title-wrap">
          <Milestone className="section-icon" />
          <div>
            <div className="milestone-title-row">
              <h3>阶段展期与跃迁终审轨 (Milestone Transition) · 7天宏观终审</h3>
              <span className={`milestone-verdict-badge ${isHarvest ? "purple" : "info"}`}>
                {isHarvest ? "🚀 达成跃迁 · 利润收割 (PROFIT_HARVEST)" : "⏳ 动能充沛 · 动态展期 +7天 (EXTENSION)"}
              </span>
            </div>
            <p>
              7天宏观周期平滑终审 · 消除周末周期性波动，综合评判核心词排位达成率、边际 CAC 与全盘效益
            </p>
          </div>
        </div>
        <button
          type="button"
          className="secondary-button compact"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "收起详情" : "展开详情"}
        </button>
      </div>

      <div className="milestone-transition-kpis">
        <div className={`milestone-kpi-card ${macro.current_tacos <= macro.target_max_tacos ? "success" : "warning"}`}>
          <span className="kpi-label">7天全盘订单与宏观 TACOS</span>
          <strong className="kpi-value">
            {macro.total_orders_7d ?? 0} 件 ({actionSymbol}{(macro.total_sales_7d ?? 0).toFixed(2)})
          </strong>
          <span className="kpi-sub">
            TACOS {(macro.current_tacos * 100).toFixed(2)}% (目标上限: {(macro.target_max_tacos * 100).toFixed(2)}%)
            {macro.macro_acos != null && ` · 广告 ACOS ${(macro.macro_acos * 100).toFixed(2)}%`}
          </span>
        </div>

        <div className={`milestone-kpi-card ${(rank.target_achievement_rate_pct ?? 0) >= 80 ? "success" : "info"}`}>
          <span className="kpi-label">核心攻坚词排位达成率</span>
          <strong className="kpi-value">
            {rank.target_achievement_rate_pct ?? 100}%
          </strong>
          <span className="kpi-sub">
            {rank.target_reached_count ?? 0} / {rank.monitored_keywords_count ?? 0} 核心词已稳定占位首页
          </span>
        </div>

        <div className={`milestone-kpi-card ${econ.is_marginal_return_positive ? "success" : "warning"}`}>
          <span className="kpi-label">边际 CAC vs 单件毛利</span>
          <strong className="kpi-value">
            {econ.marginal_cac > 0 ? `${actionSymbol}${Number(econ.marginal_cac).toFixed(2)}` : "尚未增量放量"}
          </strong>
          <span className="kpi-sub">
            单件毛利 {actionSymbol}{(econ.unit_gross_profit ?? 8.0).toFixed(2)} · {econ.is_marginal_return_positive ? "✅ 正向造血" : "⚠️ 边际饱和"}
          </span>
        </div>

        <div className={`milestone-kpi-card ${isHarvest ? "purple" : "info"}`}>
          <span className="kpi-label">宏观战略终审决议</span>
          <strong className="kpi-value">
            {isHarvest ? "平滑跃迁至利润收割" : "触发 +7 天动态展期"}
          </strong>
          <span className="kpi-sub">
            {isHarvest ? "启动阶梯式降价 -10% 锁定自然利润" : "继续保持现有放量攻坚节奏"}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="milestone-transition-details">
          <div className="milestone-rationale-box">
            <h4>📋 阶段终审判定依据与战略蓝图</h4>
            <p className="milestone-text">
              {report?.decision_rationale || (isHarvest ? "核心攻坚词已全面占领首页自然排位，全盘 TACOS 与自然单占比达标，仲裁平滑跃迁至利润收割期 (PROFIT_HARVEST)，阶梯式平滑降价以锁定最大化自然单净利。" : "核心大词跃迁动能充沛且边际收益健康，仲裁触发 +7 天动态展期，顺延一个 7 天评估周期继续扩大战果。")}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

export function ExperimentReviewSection({
  experiments,
  defaultExpanded = true,
}: {
  experiments: Experiment[];
  defaultExpanded?: boolean;
}) {
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    for (const exp of experiments) {
      map[exp.experiment_id] = defaultExpanded;
    }
    return map;
  });

  const toggleAll = (expand: boolean) => {
    const next: Record<string, boolean> = {};
    for (const exp of experiments) {
      next[exp.experiment_id] = expand;
    }
    setExpandedMap(next);
  };

  const toggleItem = (id: string) => {
    setExpandedMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const total = experiments.length;
  const observing = experiments.filter((e) => {
    const s = e.latest_evaluation?.classification ?? e.state;
    return ["OBSERVING", "TOO_EARLY"].includes(s);
  }).length;
  const improved = experiments.filter((e) => {
    const s = e.latest_evaluation?.classification ?? e.state;
    return s === "IMPROVED" || e.latest_evaluation?.decision.effective === true;
  }).length;
  const extended = experiments.filter((e) => {
    const s = e.latest_evaluation?.classification ?? e.state;
    return s === "EXTENDED" || s === "INSUFFICIENT_DATA";
  }).length;
  const rollback = experiments.filter((e) => {
    return e.state !== "ROLLED_BACK" && (e.latest_evaluation?.decision.needs_rollback ?? false);
  }).length;

  return (
    <section className="experiment-review-audit-section" aria-label="实验复盘明细">
      <div className="experiment-review-audit-header">
        <div className="section-title-wrap">
          <FlaskConical className="section-icon" />
          <div>
            <h3>本轮实验复盘明细与指标比对</h3>
            <p>共评估 {total} 项投放实验 · 结合基线绩效、实时数据窗口与业务护栏给出判定</p>
          </div>
        </div>
        <div className="experiment-review-actions">
          <button
            type="button"
            className="secondary-button compact"
            onClick={() => toggleAll(true)}
          >
            全部展开
          </button>
          <button
            type="button"
            className="secondary-button compact"
            onClick={() => toggleAll(false)}
          >
            全部收起
          </button>
        </div>
      </div>

      <div className="experiment-review-kpis">
        <div className="review-kpi-card">
          <span className="kpi-label">复盘实验总数</span>
          <strong className="kpi-value">{total}</strong>
        </div>
        <div className="review-kpi-card info">
          <span className="kpi-label">早期安全观察中</span>
          <strong className="kpi-value">{observing}</strong>
        </div>
        <div className="review-kpi-card success">
          <span className="kpi-label">达标 / 优化有效</span>
          <strong className="kpi-value">{improved}</strong>
        </div>
        <div className="review-kpi-card warning">
          <span className="kpi-label">样本不足已顺延</span>
          <strong className="kpi-value">{extended}</strong>
        </div>
        <div className={`review-kpi-card ${rollback > 0 ? "danger" : ""}`}>
          <span className="kpi-label">建议回滚</span>
          <strong className="kpi-value">{rollback}</strong>
        </div>
      </div>

      <div className="experiment-review-list">
        {experiments.map((item) => (
          <ExperimentCard
            key={item.experiment_id}
            item={item}
            isExpanded={expandedMap[item.experiment_id] ?? defaultExpanded}
            onToggle={() => toggleItem(item.experiment_id)}
          />
        ))}
      </div>
    </section>
  );
}

const AiFinalRecommendation = memo(function AiFinalRecommendation({
  recommendation,
  entityIds,
  defaultExpanded = false,
}: {
  recommendation: NonNullable<
    NonNullable<
      OptimizationRecommendations["recommendation"]
    >["final_recommendation"]
  >;
  entityIds: string[];
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <article className={`ai-recommendation-document ${expanded ? "expanded" : "collapsed"}`}>
      <header
        className="ai-recommendation-header"
        onClick={() => setExpanded((prev) => !prev)}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
      >
        <span className="ai-recommendation-icon">
          <Bot />
        </span>
        <div className="ai-recommendation-title-group">
          <div className="ai-recommendation-kicker-row">
            <span className="audit-kicker">AI FINAL RECOMMENDATION</span>
            <span className="ai-recommendation-toggle-badge">
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              {expanded ? "点击折叠" : "点击展开"}
            </span>
          </div>
          <h2>AI 最终建议</h2>
        </div>
        <div className="ai-recommendation-meta" onClick={(e) => e.stopPropagation()}>
          <span>
            模型 <code>{recommendation.model_id}</code>
          </span>
          <span>
            生成 <time>{dateTime(recommendation.generated_at)}</time>
          </span>
          <span>
            响应 <code>{shortHash(recommendation.response_sha256)}</code>
          </span>
          <span>
            Artifact <code>{shortId(recommendation.artifact_id)}</code>
          </span>
        </div>
      </header>
      {expanded && (
        <MarkdownDocument>{withoutEntityIds(sanitizeAiMarkdown(recommendation.body_markdown), entityIds)}</MarkdownDocument>
      )}
    </article>
  );
});

function legacyDecisionReason(
  action: OptimizationRecommendations["actions"][number],
  source: "AI" | "DETERMINISTIC_RULES",
): string {
  const evidence = action.evidence ?? {};
  const read = (key: string) =>
    Object.entries(evidence).find(([candidate]) => candidate.toLowerCase() === key)?.[1];
  const window = formatWindowValue(read("window") ?? "当前数据", read("window_start"), read("window_end"));
  const facts = [
    read("clicks") != null ? `${read("clicks")} 次点击` : null,
    read("orders") != null ? `${read("orders")} 个订单` : null,
    read("spend") != null ? `花费 ${formatMoneyValue(read("spend"))}` : null,
    read("sales") != null ? `销售额 ${formatMoneyValue(read("sales"))}` : null,
  ].filter(Boolean).join("、");
  const values = `建议从 ${compactValue(action.current_value)} 调整至 ${compactValue(action.constrained_value)}，并保留 ${compactValue(action.rollback_value)} 作为回滚值`;
  const provenance = source === "AI"
    ? "该历史运行未保存动作级 AI 决策理由"
    : "该动作由规则引擎生成，历史运行未保存动作级理由";
  const approvalContext = source === "AI" ? "右侧证据与 AI 最终建议" : "右侧证据与底层技术审计信息";
  return `${window}窗口${facts ? `记录了${facts}` : "的业务证据已列于右侧"}。${provenance}；${values}，审批时请结合${approvalContext}核验。`;
}

const MarkdownDocument = memo(function MarkdownDocument({ children }: { children: string }) {
  return (
    <div className="ai-recommendation-body">
      <Markdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        components={{
          a: ({ node: _node, ...props }) => (
            <a {...props} rel="noreferrer noopener" target="_blank" />
          ),
          table: ({ node: _node, ...props }) => (
            <div className="markdown-table-scroll">
              <table {...props} />
            </div>
          ),
          td: ({ node: _node, children: tdChildren, ...props }) => {
            const decodePipes = (val: ReactNode): ReactNode => {
              if (typeof val === "string") return val.replace(/&#124;/g, "|");
              if (Array.isArray(val)) return val.map((item, index) => <Fragment key={index}>{decodePipes(item)}</Fragment>);
              return val;
            };
            return <td {...props}>{decodePipes(tdChildren)}</td>;
          },
          strong: ({ node: _node, children: strongChildren, ...props }) => {
            const decodePipes = (val: ReactNode): ReactNode => {
              if (typeof val === "string") return val.replace(/&#124;/g, "|");
              if (Array.isArray(val)) return val.map((item, index) => <Fragment key={index}>{decodePipes(item)}</Fragment>);
              return val;
            };
            return <strong {...props}>{decodePipes(strongChildren)}</strong>;
          },
          code: ({ node: _node, children: codeChildren, ...props }) => {
            const decodePipes = (val: ReactNode): ReactNode => {
              if (typeof val === "string") {
                return val.replace(/&#124;/g, "|");
              }
              if (Array.isArray(val)) {
                return val.map((item, index) => <Fragment key={index}>{decodePipes(item)}</Fragment>);
              }
              return val;
            };

            return <code {...props}>{decodePipes(codeChildren)}</code>;
          },
        }}
      >
        {formatAcosText(sanitizeMarkdownTables(children))}
      </Markdown>
    </div>
  );
});

export function ExecutionResultsView({
  data,
}: {
  data: RunExecutionDetails | null;
}) {
  if (!data) return <AuditLoading />;
  if (!data.available || !data.batch)
    return (
      <AuditEmpty
        icon={<FileCheck2 />}
        title="尚无执行批次"
        detail={data.unavailable_reason === "NO_REAL_EXECUTION_BATCH"
          ? "当前仅有计划或旧审计记录，未发现 SafeExecutor 产生的真实 MAP 写入与读回证据。"
          : "Optimization Plan 尚未冻结审批或进入 Executor"}
      />
    );
  const verified = data.actions.filter((action) =>
    [
      "APPLIED_VERIFIED",
      "APPLIED_VALUE_NORMALIZED",
      "ALREADY_APPLIED",
    ].includes(action.status),
  ).length;
  const failed = data.actions.filter((action) =>
    ["FAILED", "VERIFY_MISMATCH", "STALE_PLAN", "DUPLICATE_CONFLICT"].includes(
      action.status,
    ),
  ).length;
  return (
    <div className="audit-view">
      <section
        className={`execution-overview ${data.batch.execution_mode === "SIMULATED" ? "simulated" : ""}`}
      >
        <div className="execution-heading">
          <span className="audit-kicker">
            <ShieldCheck />
            EXECUTION BATCH
          </span>
          <div>
            <h2>
              {data.batch.execution_mode === "SIMULATED"
                ? "模拟执行记录"
                : "MAP 执行结果"}
            </h2>
            <AuditStatus value={data.batch.status} />
          </div>
          <p className="mono">{data.batch.execution_batch_id}</p>
        </div>
        <div className="audit-facts execution-facts">
          <Fact label="模式" value={modeLabel(data.batch.execution_mode)} />
          <Fact label="动作" value={String(data.actions.length)} />
          <Fact label="已验证" value={String(verified)} />
          <Fact label="失败" value={String(failed)} danger={failed > 0} />
          <Fact label="开始" value={dateTime(data.batch.started_at)} />
          <Fact label="完成" value={dateTime(data.batch.completed_at)} />
        </div>
        {data.approval && (
          <div className="approval-audit">
            <span>
              {data.approval.automated ? "配置人" : "批准人"} <strong>{data.approval.approved_by ?? "-"}</strong>
            </span>
            <span>
              {data.approval.automated ? "自动授权" : "批准时间"} <strong>{dateTime(data.approval.approved_at)}</strong>
            </span>
            <span className="approval-quote">
              {data.approval.user_approval_quote ?? "-"}
            </span>
          </div>
        )}
        {(data.batch.failure_code || data.batch.failure_message) && (
          <Failure
            code={data.batch.failure_code}
            message={data.batch.failure_message}
          />
        )}
      </section>

      <section className="audit-list" aria-label="执行动作">
        {data.actions.map((action) => (
          <article className="execution-action" key={action.action_id}>
            <header className="audit-action-header">
              <span className="audit-ordinal">
                {String(action.ordinal + 1).padStart(2, "0")}
              </span>
              <div>
                <h3>
                  {resolveActionCardHeader(action, true)}
                </h3>
                <p>
                  {action.campaign_name ?? shortId(action.action_id)}
                  {action.entity && <span className="action-entity-meta"> · {entityMetaLabel(action.entity, action.resource_type)}</span>}
                </p>
              </div>
              <div className="audit-action-status">
                <AuditStatus value={action.status} />
              </div>
            </header>
            {isNegativeResourceType(action.resource_type) && action.operation?.toLowerCase() === "create" ? (
              <div className="execution-values negative-action">
                <ValueCell
                  label={negativeExecutionLabel(action.resource_type, action.operation)}
                  value={negativeDisplayValue(action.expected_after_value ?? action.request_payload, action.resource_type)}
                />
                <ArrowRight className="value-arrow" size={18} />
                <ValueCell
                  label="实时确认"
                  value={negativeDisplayValue(action.verified_after_value, action.resource_type)}
                  emphasized={action.verified_after_value != null}
                />
              </div>
            ) : isNegativeResourceType(action.resource_type) ? (
              <div className="execution-values">
                <ValueCell
                  label={action.operation?.toLowerCase() === "delete" ? "删除前" : "当前否定项"}
                  value={negativeDisplayValue(action.before_value, action.resource_type)}
                />
                <ArrowRight className="value-arrow" size={18} />
                <ValueCell
                  label={action.operation?.toLowerCase() === "delete" ? "删除结果" : "预期否定项"}
                  value={negativeDisplayValue(action.expected_after_value, action.resource_type)}
                />
                <ArrowRight className="value-arrow" size={18} />
                <ValueCell
                  label="实时确认"
                  value={negativeDisplayValue(action.verified_after_value, action.resource_type)}
                  emphasized={action.verified_after_value != null}
                />
              </div>
            ) : isCreateCampaign(action as any) ? (
              <div className="execution-values negative-action">
                <ValueCell
                  label="新建广告活动"
                  value={formatCreateCampaignValue(action.expected_after_value ?? action.request_payload, action as any, false)}
                />
                <ArrowRight className="value-arrow" size={18} />
                <ValueCell
                  label="实时确认"
                  value={
                    action.verified_after_value != null
                      ? formatCreateCampaignValue(action.verified_after_value, action as any, true)
                      : "已下发创建"
                  }
                  emphasized={action.verified_after_value != null}
                />
              </div>
            ) : isCreateAdGroup(action as any) ? (
              <div className="execution-values negative-action">
                <ValueCell
                  label="新建广告组"
                  value={formatCreateAdGroupValue(action.expected_after_value ?? action.request_payload, action as any, false)}
                />
                <ArrowRight className="value-arrow" size={18} />
                <ValueCell
                  label="实时确认"
                  value={
                    action.verified_after_value != null
                      ? formatCreateAdGroupValue(action.verified_after_value, action as any, true)
                      : "已下发创建"
                  }
                  emphasized={action.verified_after_value != null}
                />
              </div>
            ) : isCreateProductAd(action as any) ? (
              <div className="execution-values negative-action">
                <ValueCell
                  label="添加推广商品"
                  value={formatCreateProductAdValue(action.expected_after_value ?? action.request_payload, action as any, false)}
                />
                <ArrowRight className="value-arrow" size={18} />
                <ValueCell
                  label="实时确认"
                  value={
                    action.verified_after_value != null
                      ? formatCreateProductAdValue(action.verified_after_value, action as any, true)
                      : "已下发创建"
                  }
                  emphasized={action.verified_after_value != null}
                />
              </div>
            ) : action.operation?.toLowerCase() === "create" ? (
              <div className="execution-values negative-action">
                <ValueCell
                  label={String(action.resource_type ?? "").includes("keyword") ? "新增关键词" : "新增商品投放"}
                  value={
                    String(action.resource_type ?? "").includes("keyword")
                      ? formatCreateKeywordValue(action.expected_after_value ?? action.request_payload, action as any, false)
                      : formatCreateTargetValue(action.expected_after_value ?? action.request_payload, action as any, false)
                  }
                />
                <ArrowRight className="value-arrow" size={18} />
                <ValueCell
                  label="实时确认"
                  value={
                    action.verified_after_value != null
                      ? String(action.resource_type ?? "").includes("keyword")
                        ? formatCreateKeywordValue(action.verified_after_value, action as any, true)
                        : formatCreateTargetValue(action.verified_after_value, action as any, true)
                      : "未确认"
                  }
                  emphasized={action.verified_after_value != null}
                />
              </div>
            ) : isStatePauseAction(action as any) ? (
              <div className="execution-values">
                <ValueCell label="执行前状态" value="开启 (ENABLED)" />
                <ArrowRight className="value-arrow" size={18} />
                <ValueCell label="预期状态" value="暂停 (PAUSED)" />
                <ArrowRight className="value-arrow" size={18} />
                <ValueCell
                  label="实时读回"
                  value={action.verified_after_value ? "已暂停 (PAUSED)" : "未确认"}
                  emphasized={action.verified_after_value != null}
                />
              </div>
            ) : isBudgetAction(action as any) ? (
              <div className="execution-values">
                <ValueCell label="修改前日预算" value={formatBudgetDisplayValue(action.before_value)} />
                <ArrowRight className="value-arrow" size={18} />
                <ValueCell label="预期日预算" value={formatBudgetDisplayValue(action.expected_after_value ?? action.request_payload)} />
                <ArrowRight className="value-arrow" size={18} />
                <ValueCell
                  label="实时读回"
                  value={formatBudgetDisplayValue(action.verified_after_value)}
                  emphasized={action.verified_after_value != null}
                />
              </div>
            ) : isPlacementAction(action as any) ? (
              <div className="execution-values">
                <ValueCell label="修改前位置溢价" value={formatPlacementDisplayValue(action.before_value)} />
                <ArrowRight className="value-arrow" size={18} />
                <ValueCell label="预期位置溢价" value={formatPlacementDisplayValue(action.expected_after_value ?? action.request_payload)} />
                <ArrowRight className="value-arrow" size={18} />
                <ValueCell
                  label="实时读回"
                  value={formatPlacementDisplayValue(action.verified_after_value)}
                  emphasized={action.verified_after_value != null}
                />
              </div>
            ) : (
              <div className="execution-values">
                <ValueCell label="修改前" value={bidOnlyValue(action.before_value) ?? action.before_value} />
                <ArrowRight className="value-arrow" size={18} />
                <ValueCell label="预期结果" value={bidOnlyValue(action.expected_after_value) ?? action.expected_after_value} />
                <ArrowRight className="value-arrow" size={18} />
                <ValueCell
                  label="实时读回"
                  value={bidOnlyValue(action.verified_after_value) ?? action.verified_after_value}
                  emphasized={action.verified_after_value != null}
                />
              </div>
            )}
            {(action.failure_code || action.failure_message) && (
              <Failure
                code={action.failure_code}
                message={action.failure_message}
              />
            )}
            <ExecutionTraceSection action={action} />
          </article>
        ))}
      </section>
      {data.changelog.length > 0 && (
        <section className="batch-changelog">
          <div className="subsection-title">
            <FileCheck2 size={16} />
            <span>批次 Changelog 记录</span>
            <b>{data.changelog.length}</b>
          </div>
          <div className="trace-list">
            {data.changelog.map((entry) => (
              <TraceAccordionItem
                key={entry.changelog_entry_id}
                icon={<FileCheck2 size={14} />}
                title={`Changelog · ${entry.external_change_id ?? shortId(entry.changelog_entry_id)}`}
                badge={entry.status}
                data={entry.payload}
                timestamp={entry.observed_at}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Fact({
  label,
  value,
  danger = false,
}: {
  label: string;
  value: string;
  danger?: boolean;
}) {
  return (
    <span className={danger ? "danger" : ""}>
      <small>{label}</small>
      <strong>{value}</strong>
    </span>
  );
}
const AUDIT_STATUS_LABELS: Record<string, string> = {
  // 风险等级 (Risk)
  LOW: "低风险",
  MEDIUM: "中风险",
  HIGH: "高风险",
  CRITICAL: "极高风险",

  // 动作与计划状态 (Status)
  PROPOSED: "待审批",
  APPROVAL_REQUIRED: "待审批",
  APPROVED: "已审批",
  REJECTED: "已驳回",
  EXECUTED: "已执行",
  APPLIED: "已应用",
  APPLIED_VERIFIED: "已验证",
  FAILED: "执行失败",
  SKIPPED: "已跳过",
  ROLLED_BACK: "已回滚",
  PENDING: "待处理",
  SUCCESS: "成功",
};

export function auditStatusText(value: string): string {
  const upper = String(value).trim().toUpperCase();
  return AUDIT_STATUS_LABELS[upper] ?? value;
}

function AuditStatus({ value }: { value: string }) {
  return (
    <span
      className={`status status-${value.toLowerCase().replaceAll("_", "-")}`}
    >
      {auditStatusText(value)}
    </span>
  );
}
export function MicroValue({ value }: { value: unknown }) {
  if (value == null) return <span className="micro-val">-</span>;
  if (typeof value !== "object") {
    const str = String(value);
    if (str.includes("\n")) {
      return (
        <span className="micro-val multi-line" style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {str.split("\n").map((line, idx) => (
            <span key={idx} className="micro-line" style={{ display: "block" }}>
              {line}
            </span>
          ))}
        </span>
      );
    }
    return <span className="micro-val">{str}</span>;
  }
  if (Array.isArray(value)) {
    return (
      <span className="micro-val">
        {value.length
          ? value
              .map((v) =>
                typeof v === "object" && v !== null
                  ? JSON.stringify(v)
                  : String(v),
              )
              .join(", ")
          : "[]"}
      </span>
    );
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (!entries.length) return <span className="micro-val">{"{}"}</span>;

  return (
    <span className="micro-value-container">
      {entries.map(([k, v], idx) => {
        const formattedKey = k.charAt(0).toUpperCase() + k.slice(1).toLowerCase();
        return (
          <span key={k} className="micro-pair">
            {idx > 0 && <span className="micro-dot"> · </span>}
            <span className="micro-key">{formattedKey}:</span>{" "}
            <span className="micro-val">
              {typeof v === "object" && v !== null
                ? JSON.stringify(v)
                : String(v)}
            </span>
          </span>
        );
      })}
    </span>
  );
}

function ValueCell({
  label,
  value,
  emphasized = false,
  fallback = false,
}: {
  label: string;
  value: unknown;
  emphasized?: boolean;
  fallback?: boolean;
}) {
  return (
    <div
      className={`value-cell ${emphasized ? "emphasized" : ""} ${fallback ? "fallback" : ""}`}
    >
      <span className="value-cell-label">{label}</span>
      <code className="value-cell-code">
        <MicroValue value={value} />
      </code>
    </div>
  );
}

function cleanRollbackValue(value: unknown): unknown {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  const obj = value as Record<string, unknown>;
  if ("bid" in obj && obj.bid != null) {
    return { Bid: obj.bid };
  }
  if ("action" in obj && obj.action === "RESTORE_BID" && "bid" in obj) {
    return { Bid: obj.bid };
  }
  if ("state" in obj && obj.state != null) {
    return { State: obj.state };
  }
  if ("action" in obj && (obj.action === "ENABLE_KEYWORD" || obj.action === "PAUSE_OR_REMOVE")) {
    return { State: "ENABLED" };
  }
  if ("daily_budget" in obj || "dailyBudget" in obj) {
    return { Daily_budget: obj.daily_budget ?? obj.dailyBudget };
  }
  return value;
}

function bidOnlyValue(value: unknown): { Bid: unknown } | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const bidEntry = Object.entries(value as Record<string, unknown>)
    .find(([key]) => key.toLowerCase() === "bid");
  return bidEntry ? { Bid: bidEntry[1] } : null;
}

const NEGATIVE_RESOURCE_TYPES = new Set([
  "sp_negative_keywords",
  "sp_campaign_negative_keywords",
  "sp_negative_targets",
  "sp_campaign_negative_targets",
]);

function isNegativeResourceType(resourceType: string | null | undefined): boolean {
  return NEGATIVE_RESOURCE_TYPES.has(String(resourceType ?? ""));
}

function sanitizeNegativeValue(value: unknown, resourceType?: string | null): unknown {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return value;
  }
  if (isNegativeResourceType(resourceType)) {
    const recordValue = { ...(value as Record<string, unknown>) };
    delete recordValue.match_type;
    delete recordValue.matchType;
    delete recordValue.matchtype;
    delete recordValue.MATCH_TYPE;
    return recordValue;
  }
  return value;
}

function isStatePauseAction(action?: {
  proposed_value?: unknown;
  constrained_value?: unknown;
  current_value?: unknown;
  payload?: unknown;
  request_payload?: unknown;
  expected_after_value?: unknown;
  before_value?: unknown;
  verified_after_value?: unknown;
  intent?: string | null;
} | null): boolean {
  if (!action) return false;
  const check = (val: unknown): boolean => {
    if (!val || typeof val !== "object") return false;
    const rec = val as Record<string, unknown>;
    const state = String(rec.state ?? rec.State ?? "").toUpperCase();
    return state === "PAUSED" || state === "ARCHIVED";
  };
  return (
    check(action.constrained_value) ||
    check(action.proposed_value) ||
    check(action.payload) ||
    check(action.request_payload) ||
    check(action.expected_after_value) ||
    check(action.verified_after_value) ||
    String(action.intent ?? "").includes("PAUSE") ||
    String(action.intent ?? "").includes("PRUNE")
  );
}

function getLiveBid(val: unknown): number | null {
  if (!val || typeof val !== "object") return null;
  const rec = val as Record<string, unknown>;
  const b =
    rec.bid ??
    rec.actual_bid ??
    rec.live_bid ??
    rec.applied_bid ??
    rec.cpc_bid ??
    rec.target_bid ??
    rec.keyword_bid;
  if (b != null && !Number.isNaN(Number(b))) return Number(b);
  return null;
}

function formatCreateKeywordValue(
  value: unknown,
  action?: { entity?: ActionEntity | null; currency?: string; verified_after_value?: unknown; readbacks?: ExecutionReadback[] } | null,
  isConfirmed = false
): string {
  if (!value || typeof value !== "object") return "-";
  const rec = value as Record<string, unknown>;
  const kw =
    rec.keyword_text ??
    rec.keywordText ??
    rec.keyword ??
    rec.query ??
    action?.entity?.keyword_text ??
    action?.entity?.display_label;
  const matchType =
    rec.match_type ??
    rec.matchType ??
    action?.entity?.match_type;
  const matchTypeStr = String(matchType ?? "").toUpperCase();
  const matchLabel =
    matchTypeStr === "EXACT"
      ? "精准匹配"
      : matchTypeStr === "PHRASE"
      ? "词组匹配"
      : matchTypeStr === "BROAD"
      ? "广泛匹配"
      : matchTypeStr || "精准匹配";

  const currencySymbol = action?.currency === "GBP" ? "£" : action?.currency === "USD" ? "$" : "€";

  if (isConfirmed) {
    const latestReadbackValue = action?.readbacks && action.readbacks.length > 0
      ? action.readbacks[action.readbacks.length - 1].observed_value
      : null;
    const actualBidNum =
      getLiveBid(action?.verified_after_value) ??
      getLiveBid(latestReadbackValue) ??
      getLiveBid(value);
    const bidStr = actualBidNum != null ? `${currencySymbol}${actualBidNum.toFixed(2)}` : null;

    const line1 = kw ? `关键词「${kw}」` : null;
    const line2 = [
      "状态: 开启 (ENABLED)",
      bidStr ? `投放竞价: ${bidStr}` : null,
      matchLabel
    ].filter(Boolean).join(" · ");
    return [line1, line2].filter(Boolean).join("\n");
  }

  const proposedBidNum =
    rec.suggested_bid ??
    rec.proposed_bid ??
    rec.recommend_bid ??
    rec.bid ??
    getLiveBid(value);
  const bidStr = proposedBidNum != null && !Number.isNaN(Number(proposedBidNum))
    ? `${currencySymbol}${Number(proposedBidNum).toFixed(2)}`
    : null;

  return [
    kw ? `关键词「${kw}」` : null,
    matchLabel,
    bidStr ? `建议竞价 ${bidStr}` : null
  ].filter(Boolean).join(" · ");
}

function formatCreateTargetValue(
  value: unknown,
  action?: { entity?: ActionEntity | null; currency?: string; verified_after_value?: unknown; readbacks?: ExecutionReadback[] } | null,
  isConfirmed = false
): string {
  if (!value || typeof value !== "object") return "-";
  const rec = value as Record<string, unknown>;
  const asin =
    rec.asin ??
    rec.target_asin ??
    rec.value ??
    action?.entity?.asin ??
    action?.entity?.display_label;

  const currencySymbol = action?.currency === "GBP" ? "£" : action?.currency === "USD" ? "$" : "€";

  if (isConfirmed) {
    const latestReadbackValue = action?.readbacks && action.readbacks.length > 0
      ? action.readbacks[action.readbacks.length - 1].observed_value
      : null;
    const actualBidNum =
      getLiveBid(action?.verified_after_value) ??
      getLiveBid(latestReadbackValue) ??
      getLiveBid(value);
    const bidStr = actualBidNum != null ? `${currencySymbol}${actualBidNum.toFixed(2)}` : null;

    const line1 = asin ? `ASIN ${asin}` : null;
    const line2 = [
      "状态: 开启 (ENABLED)",
      bidStr ? `投放竞价: ${bidStr}` : null
    ].filter(Boolean).join(" · ");
    return [line1, line2].filter(Boolean).join("\n");
  }

  const proposedBidNum =
    rec.suggested_bid ??
    rec.proposed_bid ??
    rec.recommend_bid ??
    rec.bid ??
    getLiveBid(value);
  const bidStr = proposedBidNum != null && !Number.isNaN(Number(proposedBidNum))
    ? `${currencySymbol}${Number(proposedBidNum).toFixed(2)}`
    : null;

  return [
    asin ? `ASIN ${asin}` : null,
    bidStr ? `建议竞价 ${bidStr}` : null
  ].filter(Boolean).join(" · ");
}

function isCreateCampaign(action?: { resource_type?: string | null; operation?: string | null; payload?: unknown; proposed_value?: unknown } | null): boolean {
  if (!action) return false;
  const rt = String(action.resource_type ?? "").toLowerCase();
  const op = String(action.operation ?? "").toLowerCase();
  return (rt === "sp_campaigns" || rt === "campaign" || rt.includes("campaign")) && (op === "create" || (!op && (action.payload as any)?.name));
}

function isCreateAdGroup(action?: { resource_type?: string | null; operation?: string | null; payload?: unknown; proposed_value?: unknown } | null): boolean {
  if (!action) return false;
  const rt = String(action.resource_type ?? "").toLowerCase();
  const op = String(action.operation ?? "").toLowerCase();
  return (rt === "sp_ad_groups" || rt.includes("ad_group") || rt.includes("adgroup")) && (op === "create" || !op || (action.payload as any)?.adGroupName);
}

function isCreateProductAd(action?: { resource_type?: string | null; operation?: string | null; payload?: unknown; proposed_value?: unknown } | null): boolean {
  if (!action) return false;
  const rt = String(action.resource_type ?? "").toLowerCase();
  const op = String(action.operation ?? "").toLowerCase();
  return (rt === "sp_product_ads" || rt.includes("product_ad") || rt.includes("productad")) && (op === "create" || !op || (action.payload as any)?.sku);
}

function formatCreateCampaignValue(
  value: unknown,
  action?: { entity?: ActionEntity | null; currency?: string; verified_after_value?: unknown; payload?: unknown; proposed_value?: unknown; decision_reason?: string | null; readbacks?: ExecutionReadback[] } | null,
  isConfirmed = false
): string {
  if (!value || typeof value !== "object") return "-";
  const rec = value as Record<string, unknown>;
  const name = String(rec.name ?? rec.campaign_name ?? rec.campaignName ?? (action?.payload as any)?.name ?? action?.entity?.display_label ?? "新建广告活动");
  const rawBudget = rec.daily_budget
    ?? rec.dailyBudget
    ?? rec.budget
    ?? (action?.payload as any)?.dailyBudget
    ?? (action?.payload as any)?.budget;
  const budgetRecord = rawBudget && typeof rawBudget === "object" && !Array.isArray(rawBudget)
    ? rawBudget as Record<string, unknown>
    : undefined;
  const parsedBudget = Number(budgetRecord?.budget ?? budgetRecord?.dailyBudget ?? budgetRecord?.daily_budget ?? rawBudget ?? 10);
  const budget = Number.isFinite(parsedBudget) ? parsedBudget : 10;
  const targeting = String(rec.targeting ?? rec.targetingType ?? (action?.payload as any)?.targetingType ?? "MANUAL");
  const currencySymbol = action?.currency === "GBP" ? "£" : action?.currency === "USD" ? "$" : "€";
  const targetingLabel = targeting === "MANUAL" ? "手动投放" : targeting === "AUTO" ? "自动投放" : targeting;
  const state = String(rec.state ?? rec.status ?? rec.campaignStatus ?? "").toUpperCase();
  const stateLabel = state === "ENABLED" ? "开启 (ENABLED)" : state === "PAUSED" ? "暂停 (PAUSED)" : state === "ARCHIVED" ? "归档 (ARCHIVED)" : null;

  if (isConfirmed) {
    return `${stateLabel ? `状态: ${stateLabel} · ` : ""}活动「${name}」 · 日预算: ${currencySymbol}${budget.toFixed(2)} / 日 · ${targetingLabel}`;
  }
  return `活动「${name}」 · 日预算: ${currencySymbol}${budget.toFixed(2)} / 日 · ${targetingLabel}`;
}

function formatCreateAdGroupValue(
  value: unknown,
  action?: { entity?: ActionEntity | null; currency?: string; verified_after_value?: unknown; payload?: unknown; proposed_value?: unknown; decision_reason?: string | null; readbacks?: ExecutionReadback[] } | null,
  isConfirmed = false
): string {
  if (!value || typeof value !== "object") return "-";
  const rec = value as Record<string, unknown>;
  const name = String(rec.ad_group_name ?? rec.adGroupName ?? (action?.payload as any)?.adGroupName ?? "新建广告组");
  const defaultBid = Number(rec.default_bid ?? rec.defaultBid ?? (action?.payload as any)?.defaultBid ?? 1.00);
  const currencySymbol = action?.currency === "GBP" ? "£" : action?.currency === "USD" ? "$" : "€";

  if (isConfirmed) {
    return `状态: 开启 (ENABLED) · 广告组「${name}」 · 默认竞价: ${currencySymbol}${defaultBid.toFixed(2)}`;
  }
  return `广告组「${name}」 · 默认竞价: ${currencySymbol}${defaultBid.toFixed(2)}`;
}

function formatCreateProductAdValue(
  value: unknown,
  action?: { entity?: ActionEntity | null; currency?: string; verified_after_value?: unknown; payload?: unknown; proposed_value?: unknown; evidence?: Record<string, unknown> | null; decision_reason?: string | null; readbacks?: ExecutionReadback[] } | null,
  isConfirmed = false
): string {
  if (!value || typeof value !== "object") return "-";
  const rec = value as Record<string, unknown>;
  const asin = String(rec.asin ?? (action?.payload as any)?.asin ?? action?.entity?.asin ?? action?.evidence?.asin ?? "ASIN");
  const sku = String(rec.sku ?? (action?.payload as any)?.sku ?? (action?.entity as any)?.sku ?? action?.evidence?.sku ?? "-");
  const adGroupName = String(rec.adGroupName ?? rec.ad_group_name ?? (action?.payload as any)?.adGroupName ?? "-");

  if (isConfirmed) {
    return `状态: 开启 (ENABLED) · 推广商品 ASIN: ${asin} ${sku !== "-" ? `(SKU: ${sku})` : ""}`;
  }
  return `推广商品 ASIN: ${asin} ${sku !== "-" ? `(SKU: ${sku})` : ""} ${adGroupName !== "-" ? `· 广告组: ${adGroupName}` : ""}`;
}

function resolveActionCardHeader(action: {
  operation?: string | null;
  resource_type?: string | null;
  entity?: ActionEntity | null;
  proposed_value?: unknown;
  constrained_value?: unknown;
  payload?: unknown;
  intent?: string | null;
  campaign_name?: string | null;
  decision_reason?: string | null;
}, isExecution = false): string {
  const rt = String(action.resource_type ?? "").toLowerCase();

  if (isCreateCampaign(action)) {
    return "CREATE · 广告活动 · 新建广告活动";
  }
  if (isCreateAdGroup(action)) {
    return "CREATE · 广告组 · 新建广告组";
  }
  if (isCreateProductAd(action)) {
    return "CREATE · 推广商品 · 添加推广商品 (SKU / ASIN)";
  }
  if (isBudgetAction(action)) {
    return "BUDGET · 广告活动 · 日预算调整";
  }
  if (isPlacementAction(action)) {
    return "PLACEMENT · 广告活动 · 位置溢价调整";
  }

  const kind = isExecution ? executionEntityKindLabel(action.entity, action.resource_type) : entityKindLabel(action.entity);
  const fallbackLabel = rt.includes("keyword") ? "关键词" : rt.includes("target") ? "投放目标" : "广告实体";
  let display = entityDisplayLabel(action.entity, fallbackLabel, { campaign_name: action.campaign_name, decision_reason: action.decision_reason });

  if (rt.includes("target")) {
    const extractedAsin = extractAsinFromAction(action);
    if (display === "ASIN" || display === "投放目标" || /^\d{10,}$/.test(display)) {
      if (extractedAsin) {
        display = extractedAsin;
      } else {
        const cName = String(action.campaign_name || "").toUpperCase();
        if (cName.includes("LOOSE") || cName.includes("宽泛")) display = "自动投放 · 宽泛匹配";
        else if (cName.includes("CLOSE") || cName.includes("紧密")) display = "自动投放 · 紧密匹配";
        else if (cName.includes("SUBSTITUTE") || cName.includes("同类")) display = "自动投放 · 同类商品";
        else if (cName.includes("COMPLEMENT") || cName.includes("关联")) display = "自动投放 · 关联商品";
        else if (cName.includes("PAT") || cName.includes("COMPETITOR")) display = "商品投放";
      }
    }
  }

  return `${actionOperationLabel(action.operation, action.resource_type, action)} · ${kind} · ${display}`;
}

function isBudgetAction(action?: {
  intent?: string | null;
  operation?: string | null;
  resource_type?: string | null;
  proposed_value?: unknown;
  constrained_value?: unknown;
  current_value?: unknown;
  payload?: unknown;
  request_payload?: unknown;
  expected_after_value?: unknown;
  before_value?: unknown;
  verified_after_value?: unknown;
} | null): boolean {
  if (!action) return false;
  if (isCreateCampaign(action)) return false;
  const check = (val: unknown): boolean => {
    if (!val || typeof val !== "object") return false;
    const rec = val as Record<string, unknown>;
    return rec.daily_budget != null || rec.dailyBudget != null || rec.budget != null;
  };
  return (
    String(action.intent ?? "").includes("BUDGET") ||
    check(action.proposed_value) ||
    check(action.constrained_value) ||
    check(action.payload) ||
    check(action.current_value) ||
    check(action.request_payload) ||
    check(action.expected_after_value) ||
    check(action.before_value) ||
    check(action.verified_after_value)
  );
}

function isPlacementAction(action?: {
  intent?: string | null;
  proposed_value?: unknown;
  constrained_value?: unknown;
  current_value?: unknown;
  payload?: unknown;
  request_payload?: unknown;
  expected_after_value?: unknown;
  before_value?: unknown;
  verified_after_value?: unknown;
} | null): boolean {
  if (!action) return false;
  const check = (val: unknown): boolean => {
    if (!val || typeof val !== "object") return false;
    const rec = val as Record<string, unknown>;
    if (
      rec.placement_top_percentage != null ||
      rec.placement_product_page_percentage != null ||
      rec.placementTopPercentage != null ||
      rec.placementProductPagePercentage != null ||
      rec.placementBidding != null ||
      rec.placement_bidding != null
    ) {
      return true;
    }
    if (rec.dynamicBidding && typeof rec.dynamicBidding === "object") {
      const dyn = rec.dynamicBidding as Record<string, unknown>;
      if (dyn.placementBidding != null || dyn.placement_bidding != null) return true;
    }
    if (rec.dynamic_bidding && typeof rec.dynamic_bidding === "object") {
      const dyn = rec.dynamic_bidding as Record<string, unknown>;
      if (dyn.placementBidding != null || dyn.placement_bidding != null) return true;
    }
    return false;
  };
  return (
    String(action.intent ?? "").includes("PLACEMENT") ||
    check(action.proposed_value) ||
    check(action.constrained_value) ||
    check(action.payload) ||
    check(action.current_value) ||
    check(action.request_payload) ||
    check(action.expected_after_value) ||
    check(action.before_value) ||
    check(action.verified_after_value)
  );
}

function formatPlacementDisplayValue(val: unknown): string {
  if (!val || typeof val !== "object") return "-";
  const rec = val as Record<string, unknown>;

  let topPct: number | null = null;
  let prodPct: number | null = null;

  if (rec.placement_top_percentage != null) topPct = Number(rec.placement_top_percentage);
  else if (rec.placementTopPercentage != null) topPct = Number(rec.placementTopPercentage);

  if (rec.placement_product_page_percentage != null) prodPct = Number(rec.placement_product_page_percentage);
  else if (rec.placementProductPagePercentage != null) prodPct = Number(rec.placementProductPagePercentage);

  const extractFromList = (list: unknown) => {
    if (Array.isArray(list)) {
      for (const item of list) {
        if (!item || typeof item !== "object") continue;
        const itemRec = item as Record<string, unknown>;
        const pred = String(itemRec.predicate ?? itemRec.placement ?? "").toUpperCase();
        const pct = itemRec.percentage != null ? Number(itemRec.percentage) : itemRec.bidding_adjustment != null ? Number(itemRec.bidding_adjustment) : null;
        if (pct != null) {
          if (pred.includes("TOP") || pred.includes("SEARCH")) topPct = pct;
          if (pred.includes("PRODUCT") || pred.includes("DETAIL")) prodPct = pct;
        }
      }
    }
  };

  extractFromList(rec.placementBidding);
  extractFromList(rec.placement_bidding);
  if (rec.dynamicBidding && typeof rec.dynamicBidding === "object") {
    extractFromList((rec.dynamicBidding as any).placementBidding);
    extractFromList((rec.dynamicBidding as any).placement_bidding);
  }
  if (rec.dynamic_bidding && typeof rec.dynamic_bidding === "object") {
    extractFromList((rec.dynamic_bidding as any).placementBidding);
    extractFromList((rec.dynamic_bidding as any).placement_bidding);
  }
  const opts = (rec.optimizations ?? rec.optimization ?? rec.optimizationSettings) as any;
  if (opts && typeof opts === "object") {
    const bidSettings = opts.bidSettings ?? opts.bidsettings ?? opts.bid_settings;
    const adj = bidSettings?.bidAdjustments ?? bidSettings?.bidadjustments ?? bidSettings?.bid_adjustments;
    extractFromList(adj?.placementBidAdjustments ?? adj?.placementbidadjustments ?? adj?.placement_bid_adjustments);
  }

  if (topPct != null && prodPct != null) {
    return `首页 +${topPct}% · 详情页 +${prodPct}%`;
  }
  if (topPct != null) {
    return `首页首位 +${topPct}%`;
  }
  if (prodPct != null) {
    return `详情页 +${prodPct}%`;
  }
  return compactValue(val);
}

function formatBudgetDisplayValue(val: unknown, symbol = "€"): string {
  if (!val || typeof val !== "object") return "-";
  const rec = val as Record<string, unknown>;
  const nestedBudget = rec.budget && typeof rec.budget === "object" ? rec.budget as Record<string, unknown> : null;
  const budget = rec.daily_budget ?? rec.dailyBudget ?? nestedBudget?.budget ?? (typeof rec.budget === "number" ? rec.budget : undefined);
  if (budget != null) {
    return `${symbol}${Number(budget).toFixed(2)} / 日`;
  }
  return compactValue(val);
}

function actionOperationLabel(
  operation: string | null | undefined,
  resourceType: string | null | undefined,
  action?: { proposed_value?: unknown; constrained_value?: unknown; payload?: unknown; intent?: string | null }
): string {
  if (isNegativeResourceType(resourceType)) return "NEGATIVE";
  if (action && isStatePauseAction(action)) return "PAUSE";
  if (action && isBudgetAction(action)) return "BUDGET";
  if (action && isPlacementAction(action)) return "PLACEMENT";
  return operation?.toUpperCase() ?? "ACTION";
}

function negativeEntityKindLabel(resourceType: string | null | undefined): string {
  return String(resourceType ?? "").includes("keyword") ? "否定关键词" : "商品否定";
}

function executionEntityKindLabel(entity: ActionEntity | null | undefined, resourceType: string | null | undefined): string {
  return isNegativeResourceType(resourceType) ? negativeEntityKindLabel(resourceType) : entityKindLabel(entity);
}

function negativeExecutionLabel(resourceType: string | null | undefined, operation: string | null | undefined): string {
  const action = operation?.toLowerCase();
  const verb = action === "delete" ? "删除" : action === "update" ? "更新" : "新增";
  return `${verb}${negativeEntityKindLabel(resourceType)}`;
}

function extractNegativeProductTitle(action: {
  resource_type?: string | null;
  evidence?: Record<string, unknown> | null;
  entity?: ActionEntity | null;
  proposed_value?: unknown;
  constrained_value?: unknown;
}): string | null {
  if (String(action.resource_type ?? "").includes("keyword")) return null;

  const evidence = action.evidence;
  if (evidence && typeof evidence === "object") {
    const candidateProduct = evidence.candidate_product as Record<string, unknown> | undefined;
    if (candidateProduct && typeof candidateProduct === "object" && candidateProduct.title) {
      const t = String(candidateProduct.title).trim();
      if (t) return t;
    }
    const direct = evidence.product_title ?? evidence.title ?? evidence.asin_title ?? evidence.item_title;
    if (direct != null && String(direct).trim()) {
      return String(direct).trim();
    }
  }

  const checkValue = (val: unknown) => {
    if (val && typeof val === "object") {
      const rec = val as Record<string, unknown>;
      if (rec.title != null && String(rec.title).trim()) return String(rec.title).trim();
      if (rec.product_title != null && String(rec.product_title).trim()) return String(rec.product_title).trim();
      if (rec.asin_title != null && String(rec.asin_title).trim()) return String(rec.asin_title).trim();
    }
    return null;
  };

  const fromConstrained = checkValue(action.constrained_value);
  if (fromConstrained) return fromConstrained;
  const fromProposed = checkValue(action.proposed_value);
  if (fromProposed) return fromProposed;

  const entity = action.entity;
  if (entity?.display_label) {
    const label = String(entity.display_label).trim();
    if (
      label &&
      label !== entity.entity_id &&
      label !== entity.asin &&
      !/^B0[A-Z0-9]{8}$/i.test(label) &&
      !label.startsWith("ASIN ") &&
      !label.startsWith("自动投放")
    ) {
      return label;
    }
  }

  return "暂无商品标题";
}

function negativeDisplayValue(value: unknown, resourceType: string | null | undefined): string | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return value == null ? null : String(value);
  }
  const recordValue = value as Record<string, unknown>;
  if (String(resourceType ?? "").includes("keyword")) {
    const keyword = recordValue.keywordText ?? recordValue.keyword_text ?? recordValue.negative_keyword ?? recordValue.keyword;
    if (keyword != null && String(keyword).trim()) {
      return `关键词「${String(keyword)}」`;
    }
  } else {
    const expression = recordValue.expression ?? recordValue.target_expression;
    const expressionValue = Array.isArray(expression) ? expression[0] : expression;
    const expressionRecord = expressionValue && typeof expressionValue === "object" ? expressionValue as Record<string, unknown> : null;
    const targetValue = expressionRecord?.value ?? recordValue.asin ?? recordValue.negative_asin;
    if (targetValue != null && String(targetValue).trim()) {
      const targetType = String(expressionRecord?.type ?? recordValue.targeting_type ?? "").toUpperCase();
      return targetType.includes("ASIN") || /^B0[A-Z0-9]{8}$/i.test(String(targetValue).trim())
        ? `ASIN ${String(targetValue).trim().toUpperCase()}`
        : `目标 ${String(targetValue).trim()}`;
    }
  }
  return null;
}

function isCreateAsinTarget(action: {
  operation?: string | null;
  resource_type?: string | null;
  payload?: unknown;
  proposed_value?: unknown;
  constrained_value?: unknown;
  entity?: ActionEntity | null;
  evidence?: Record<string, unknown> | null;
}): boolean {
  if (isNegativeResourceType(action.resource_type)) return false;
  if (isCreateProductAd(action) || isCreateAdGroup(action) || isCreateCampaign(action)) return false;
  if (action.operation?.toLowerCase() !== "create") return false;
  const rt = String(action.resource_type ?? "").toLowerCase();
  if (rt.includes("keyword")) return false;
  return rt.includes("target") || extractAsinFromTargetAction(action) != null;
}

function isCreateKeyword(action: {
  operation?: string | null;
  resource_type?: string | null;
  payload?: unknown;
  proposed_value?: unknown;
  constrained_value?: unknown;
  entity?: ActionEntity | null;
  evidence?: Record<string, unknown> | null;
}): boolean {
  if (isNegativeResourceType(action.resource_type)) return false;
  if (action.operation?.toLowerCase() !== "create") return false;
  const rt = String(action.resource_type ?? "").toLowerCase();
  return rt.includes("keyword");
}

function extractAsinFromTargetAction(action: {
  payload?: unknown;
  proposed_value?: unknown;
  constrained_value?: unknown;
  entity?: ActionEntity | null;
  evidence?: Record<string, unknown> | null;
}): string | null {
  const check = (val: unknown): string | null => {
    if (!val || typeof val !== "object") return null;
    const rec = val as Record<string, unknown>;
    if (rec.target_asin) return String(rec.target_asin).trim().toUpperCase();
    if (rec.asin) return String(rec.asin).trim().toUpperCase();
    if (rec.value && /^B0[A-Z0-9]{8}$/i.test(String(rec.value).trim())) return String(rec.value).trim().toUpperCase();
    if (Array.isArray(rec.expression)) {
      const expr = rec.expression[0];
      if (expr && typeof expr === "object" && (expr as any).value) {
        return String((expr as any).value).trim().toUpperCase();
      }
    }
    return null;
  };

  const fromConstrained = check(action.constrained_value);
  if (fromConstrained) return fromConstrained;
  const fromProposed = check(action.proposed_value);
  if (fromProposed) return fromProposed;
  const fromPayload = check(action.payload);
  if (fromPayload) return fromPayload;

  if (action.entity?.asin) return String(action.entity.asin).trim().toUpperCase();
  if (action.entity?.display_label) {
    const match = /\b(B0[A-Z0-9]{8})\b/i.exec(String(action.entity.display_label));
    if (match) return match[1].toUpperCase();
  }
  if (action.evidence) {
    const rawSearch = String(action.evidence.rawSearchTerm ?? action.evidence.search_term ?? "");
    const match = /\b(B0[A-Z0-9]{8})\b/i.exec(rawSearch);
    if (match) return match[1].toUpperCase();
  }
  return null;
}

function extractKeywordFromAction(action: {
  payload?: unknown;
  proposed_value?: unknown;
  constrained_value?: unknown;
  entity?: ActionEntity | null;
  evidence?: Record<string, unknown> | null;
}): { text: string; matchType?: string | null; bid?: number | null } {
  const check = (val: unknown) => {
    if (!val || typeof val !== "object") return {};
    const rec = val as Record<string, unknown>;
    return {
      text: (rec.keyword_text ?? rec.keywordText ?? rec.keyword) as string | undefined,
      matchType: (rec.match_type ?? rec.matchType) as string | undefined,
      bid: (rec.bid ?? rec.Bid) as number | undefined,
    };
  };

  const c = check(action.constrained_value);
  const p = check(action.proposed_value);
  const pl = check(action.payload);

  const text = c.text ?? p.text ?? pl.text ?? action.entity?.keyword_text ?? action.entity?.display_label ?? "";
  const matchType = c.matchType ?? p.matchType ?? pl.matchType ?? action.entity?.match_type ?? null;
  const bid = c.bid ?? p.bid ?? pl.bid ?? null;

  return { text: String(text).trim(), matchType, bid: bid != null && Number.isFinite(Number(bid)) ? Number(bid) : null };
}

function extractBidFromTargetAction(action: {
  payload?: unknown;
  proposed_value?: unknown;
  constrained_value?: unknown;
}): number | null {
  const check = (val: unknown): number | null => {
    if (!val || typeof val !== "object") return null;
    const rec = val as Record<string, unknown>;
    if (rec.bid != null && Number.isFinite(Number(rec.bid))) return Number(rec.bid);
    if (rec.Bid != null && Number.isFinite(Number(rec.Bid))) return Number(rec.Bid);
    return null;
  };
  return check(action.constrained_value) ?? check(action.proposed_value) ?? check(action.payload) ?? null;
}

export function matchTypeDisplayLabel(matchType?: string | null): string {
  const upper = String(matchType ?? "").trim().toUpperCase();
  switch (upper) {
    case "EXACT":
      return "精准匹配";
    case "PHRASE":
      return "词组匹配";
    case "BROAD":
      return "广泛匹配";
    default:
      return matchType ? `${matchType} 匹配` : "精准匹配";
  }
}

function extractNegativeKeywordInfo(action: {
  payload?: unknown;
  proposed_value?: unknown;
  constrained_value?: unknown;
  entity?: ActionEntity | null;
  evidence?: Record<string, unknown> | null;
  decision_reason?: string | null;
}): { text: string; negativeType: string } {
  const check = (val: unknown) => {
    if (!val || typeof val !== "object") return {};
    const rec = val as Record<string, unknown>;
    return {
      text: (rec.keywordText ?? rec.keyword_text ?? rec.negative_keyword ?? rec.negative_keyword_text ?? rec.negativeKeyword ?? rec.keyword ?? rec.text ?? rec.value) as string | undefined,
      matchType: (rec.matchType ?? rec.match_type ?? rec.negative_type ?? rec.negativeType ?? rec.type) as string | undefined,
    };
  };

  const c = check(action.constrained_value);
  const p = check(action.proposed_value);
  const pl = check(action.payload);

  let text = c.text ?? p.text ?? pl.text ?? action.entity?.keyword_text ?? action.entity?.display_label ?? (action.evidence?.rawSearchTerm as string) ?? (action.evidence?.search_term as string) ?? "";
  if (!text && action.decision_reason) {
    const match = /["'「]([^"'「」]+)["'」]/.exec(action.decision_reason);
    if (match) text = match[1];
  }
  const rawType = c.matchType ?? p.matchType ?? pl.matchType ?? action.entity?.match_type ?? "";
  const upper = String(rawType).toUpperCase();
  const negativeType = upper.includes("PHRASE") || (action.decision_reason && action.decision_reason.includes("词组否定"))
    ? "词组否定"
    : "精准否定";

  return { text: String(text).trim(), negativeType };
}

function Evidence({
  data,
  hideBusiness = false,
  currency,
  campaignName,
  decisionReason,
  entity,
  defaultExpanded = false,
}: {
  data: Record<string, unknown> | null;
  hideBusiness?: boolean;
  currency?: string | null;
  campaignName?: string | null;
  decisionReason?: string | null;
  entity?: ActionEntity | null;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showMetadata, setShowMetadata] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!data || Object.keys(data).length === 0) {
    if (hideBusiness) return null;
    return (
      <div className="evidence-container">
        <div className="evidence-header">
          <span className="detail-label">证据 (Evidence)</span>
        </div>
        <span className="muted-copy">未记录</span>
      </div>
    );
  }

  const getCaseInsensitive = (key: string) => {
    const entry = Object.entries(data).find(
      ([k]) => k.toLowerCase() === key.toLowerCase()
    );
    return entry ? entry[1] : undefined;
  };

  const vatBasis = getCaseInsensitive("report_sales_vat_basis") as string | undefined;
  const isVatInc = vatBasis === "VAT_INCLUSIVE";
  const isVatExc = vatBasis === "VAT_EXCLUSIVE";
  const symbol = resolveCurrencySymbol(currency, data, campaignName, decisionReason);

  const METADATA_KEYS = new Set([
    "strategy_hash",
    "value_source",
    "report_sales_vat_basis",
    "experiment_feedback",
    "rollback_experiment_id",
    "execution_plan",
    "raw_payload",
  ]);

  const BUILTIN_BUSINESS_CLEAN_KEYS = new Set([
    "window",
    "windowstart",
    "windowend",
    "entityid",
    "clicks",
    "orders",
    "spend",
    "sales",
    "observedacos",
    "targetacos",
    "maxbiddecreasepercent",
    "reportsalesvatbasis",
    "currency",
    "campaignid",
    "adgroupid",
    "entitytype",
    "entitylabel",
    "matchtype",
    "targetingtype",
    "keywordtext",
    "asin",
    "targetasin",
    "negativeasin",
    "negativekeyword",
    "targetexpression",
    "expression",
    "campaignrole",
    "searchterm",
    "query",
    "sku",
  ]);

  const businessEntries: Array<{ key: string; label: string; value: string }> = [];
  const metadataEntries: Array<{ key: string; value: unknown }> = [];

  const windowVal = getCaseInsensitive("window");
  const windowStartVal = getCaseInsensitive("window_start");
  const windowEndVal = getCaseInsensitive("window_end");
  if (windowVal != null) {
    businessEntries.push({ key: "window", label: "WINDOW", value: formatWindowValue(windowVal, windowStartVal, windowEndVal) });
  }
  const percent = (value: unknown) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? `${(numeric * 100).toFixed(2)}%` : String(value);
  };
  const observedAcosVal = getCaseInsensitive("observed_acos");
  if (observedAcosVal != null) {
    businessEntries.push({ key: "observed_acos", label: "OBSERVED ACOS", value: percent(observedAcosVal) });
  }
  const targetAcosVal = getCaseInsensitive("target_acos");
  if (targetAcosVal != null) {
    businessEntries.push({ key: "target_acos", label: "TARGET ACOS", value: percent(targetAcosVal) });
  }
  const maxDecreaseVal = getCaseInsensitive("max_bid_decrease_percent");
  if (maxDecreaseVal != null) {
    businessEntries.push({ key: "max_bid_decrease_percent", label: "MAX WEEKLY DECREASE", value: percent(maxDecreaseVal) });
  }
  const clicksVal = getCaseInsensitive("clicks");
  if (clicksVal != null) {
    businessEntries.push({ key: "clicks", label: "CLICKS", value: String(clicksVal) });
  }
  const ordersVal = getCaseInsensitive("orders");
  if (ordersVal != null) {
    businessEntries.push({ key: "orders", label: "ORDERS", value: String(ordersVal) });
  }
  const spendVal = getCaseInsensitive("spend");
  if (spendVal != null) {
    businessEntries.push({ key: "spend", label: "SPEND", value: formatMoneyValue(spendVal, symbol) });
  }
  const salesVal = getCaseInsensitive("sales");
  if (salesVal != null) {
    const salesLabel = isVatInc ? "SALES (VAT inc.)" : isVatExc ? "SALES (VAT excl.)" : "SALES";
    businessEntries.push({ key: "sales", label: salesLabel, value: formatMoneyValue(salesVal, symbol) });
  }
  const keywordTextVal = getCaseInsensitive("keyword_text") ?? (entity?.entity_type === "keyword" ? entity.keyword_text ?? entity.display_label : undefined);
  if (keywordTextVal != null && String(keywordTextVal).trim()) {
    businessEntries.push({ key: "keyword_text", label: "KEYWORD_TEXT", value: String(keywordTextVal) });
  } else {
    const rawTargetingType =
      getCaseInsensitive("targeting_type") ??
      getCaseInsensitive("target_type") ??
      getCaseInsensitive("targetingType") ??
      getCaseInsensitive("expression_type") ??
      entity?.targeting_type ??
      (entity?.entity_type === "target" ? entity.display_label : undefined);
    const targetingTypeVal = shortTargetingTypeLabel(rawTargetingType ? String(rawTargetingType) : null);
    if (targetingTypeVal != null && String(targetingTypeVal).trim()) {
      businessEntries.push({ key: "targeting_type", label: "TARGETING_TYPE", value: String(targetingTypeVal) });
    }
  }

  // Additional custom attributes
  Object.entries(data).forEach(([k, v]) => {
    const cleanKey = k.toLowerCase().replace(/[^a-z0-9]/g, "");
    const lowerKey = k.toLowerCase();
    if (!BUILTIN_BUSINESS_CLEAN_KEYS.has(cleanKey)) {
      if (
        METADATA_KEYS.has(lowerKey) ||
        lowerKey.includes("hash") ||
        lowerKey.includes("source") ||
        lowerKey.includes("payload") ||
        (typeof v === "string" && v.length > 30) ||
        (typeof v === "object" && v !== null)
      ) {
        metadataEntries.push({ key: k, value: v });
      } else {
        businessEntries.push({ key: k, label: k.toUpperCase(), value: compactValue(v) });
      }
    } else if (METADATA_KEYS.has(lowerKey) || cleanKey === "entityid" || cleanKey === "campaignid" || cleanKey === "adgroupid") {
      metadataEntries.push({ key: k, value: v });
    }
  });

  // Retain all raw diagnostic fields in copyable metadata
  Object.entries(data).forEach(([k, v]) => {
    if (!metadataEntries.some((m) => m.key.toLowerCase() === k.toLowerCase())) {
      metadataEntries.push({ key: k, value: v });
    }
  });

  const handleCopy = async () => {
    const ok = await copyText(JSON.stringify(data, null, 2));
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="evidence-container">
      <div className="evidence-header">
        {!hideBusiness ? (
          <button
            type="button"
            className="evidence-toggle-btn"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
          >
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span className="detail-label">证据 (Evidence)</span>
            <span className="evidence-toggle-badge">
              {expanded ? "点击折叠" : `展开 ${businessEntries.length} 项数据`}
            </span>
          </button>
        ) : null}
        <div className="evidence-toolbar">
          <button
            type="button"
            className={`evidence-meta-btn ${copied ? "copied" : ""}`}
            onClick={handleCopy}
            title="复制包含 Hash 和 Source 的完整排障元数据 JSON"
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            <span>{copied ? "已复制" : "复制排障元数据"}</span>
          </button>
          <button
            type="button"
            className={`evidence-meta-btn ${showMetadata ? "active" : ""}`}
            onClick={() => setShowMetadata((prev) => !prev)}
            title="展开/折叠底层技术元数据 (Hash, Source)"
          >
            <Code2 size={12} />
            <span>{showMetadata ? "收起元数据" : "技术元数据"}</span>
          </button>
        </div>
      </div>

      {!hideBusiness && expanded && (
        <dl className="evidence-grid">
          {businessEntries.map((item) => (
            <div key={item.key}>
              <dt>{item.label}</dt>
              <dd>{item.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {showMetadata && (
        <div className="evidence-metadata-drawer">
          <div className="metadata-drawer-header">
            <span className="mono">RAW JSON METADATA (排障报文)</span>
            <button
              type="button"
              className="console-copy-btn"
              onClick={handleCopy}
              title="复制完整 JSON"
            >
              {copied ? "已复制" : "复制 JSON"}
            </button>
          </div>
          <pre className="console-pre evidence-json-pre">
            <code>{JSON.stringify(data, null, 2)}</code>
          </pre>
        </div>
      )}
    </div>
  );
}

function ExecutionTraceSection({
  action,
  defaultExpanded = false,
}: {
  action: NonNullable<RunExecutionDetails["actions"]>[number];
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const negative = isNegativeResourceType(action.resource_type);
  const totalTraces =
    (action.readbacks?.length ?? 0) +
    (action.request_payload ? 1 : 0) +
    (action.map_response ? 1 : 0) +
    (action.changelog?.length ?? 0);

  return (
    <div className={`execution-trace-section ${expanded ? "expanded" : "collapsed"}`}>
      <button
        type="button"
        className="trace-section-toggle"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        title={expanded
          ? "收起追踪与确认记录"
          : negative ? "展开查看否定项确认与网络追踪" : "展开查看实时读回与网络追踪"}
      >
        <div className="trace-section-toggle-left">
          <span className="trace-chevron">
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
          <History size={14} />
          <span>{negative ? "否定项确认与网络追踪 (Traces)" : "实时读回与网络追踪 (Traces)"}</span>
          {totalTraces > 0 && <span className="trace-count-pill">{totalTraces}</span>}
        </div>
      </button>

      {expanded && (
        <div className="trace-section-body">
          <ReadbackTimeline items={action.readbacks} resourceType={action.resource_type} intent={action.intent} />
          <div className="trace-list">
            <TraceAccordionItem
              icon={<Braces size={14} />}
              title="MAP 请求载荷 (Request Payload)"
              badge={action.request_payload != null ? "RECORDED" : "NO_DATA"}
              data={action.request_payload}
            />
            <TraceAccordionItem
              icon={<Braces size={14} />}
              title="MAP 写入响应 (Response Payload)"
              badge={action.map_response != null ? "RECORDED" : "NO_DATA"}
              data={action.map_response}
            />
            {action.changelog.length > 0 && (
              <ChangelogAccordionItem entries={action.changelog} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

type ReadbackTone = "matched" | "recovered" | "mismatch" | "pending";

function hasPlacementOnly(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  const hasPlacement = obj.dynamicBidding != null || obj.dynamic_bidding != null || Array.isArray(obj.placementBidding) || Array.isArray(obj.placementbidding);
  const hasBudget = obj.budget != null || obj.dailyBudget != null || obj.daily_budget != null;
  return hasPlacement && !hasBudget;
}


function readbackPresentation(
  item: ExecutionReadback,
  finalMatched: boolean,
  negative = false,
  resourceType?: string | null,
  intent?: string | null,
): {
  tone: ReadbackTone;
  title: string;
  status: string;
} {
  const normalizedStatus = item.status.toUpperCase();
  const isPlacement =
    intent === "PLACEMENT_UPDATE" ||
    intent === "PLACEMENT_BIDDING_UPDATE" ||
    (resourceType === "sp_campaigns" && hasPlacementOnly(item.observed_value));
  const budgetAction = resourceType === "sp_campaigns" && !isPlacement;
  const isPreflight = item.attempt === 0 || normalizedStatus.startsWith("PREFLIGHT_");
  if (isPreflight && ["PREFLIGHT_MATCHED", "PREFLIGHT_READY"].includes(normalizedStatus)) {
    return {
      tone: "matched",
      title: "写入前校验",
      status: normalizedStatus === "PREFLIGHT_MATCHED" ? "基线一致" : "准备完成"
    };
  }
  if (item.matches_expected === true) {
    return {
      tone: "matched",
      title: isPreflight ? "写入前校验" : `第 ${item.attempt} 次回读`,
      status: normalizedStatus === "ALREADY_APPLIED"
        ? negative ? "否定项已存在" : isPlacement ? "位置溢价已设置" : "已是目标竞价"
        : negative ? "否定项已确认" : isPlacement ? "位置溢价已匹配" : budgetAction ? "预算已匹配" : "竞价已匹配"
    };
  }
  if (finalMatched) {
    return {
      tone: "recovered",
      title: isPreflight ? "写入前校验" : `第 ${item.attempt} 次回读`,
      status: negative ? "等待同步，后续已确认" : isPlacement ? "等待同步，后续位置溢价已成功" : budgetAction ? "等待同步，后续预算已成功" : "等待同步，后续已成功"
    };
  }
  return {
    tone: item.matches_expected === false ? "mismatch" : "pending",
    title: isPreflight ? "写入前校验" : `第 ${item.attempt} 次回读`,
    status: item.matches_expected === false
      ? negative ? "否定项未确认" : isPlacement ? "位置溢价未匹配" : budgetAction ? "预算未匹配" : "竞价未匹配"
      : negative ? "等待确认" : "等待回读"
  };
}

export function ReadbackTimeline({
  items,
  resourceType,
  intent,
}: {
  items: ExecutionReadback[];
  resourceType?: string | null;
  intent?: string | null;
}) {
  const negative = isNegativeResourceType(resourceType);
  const isPlacement =
    intent === "PLACEMENT_UPDATE" ||
    intent === "PLACEMENT_BIDDING_UPDATE" ||
    (resourceType === "sp_campaigns" && items.some((item) => hasPlacementOnly(item.observed_value)));

  if (!items.length) {
    return (
      <div className="readback-empty-state">
        <span className="muted-copy">{negative ? "尚未记录否定项确认数据" : "尚未记录读回对账数据"}</span>
      </div>
    );
  }

  const finalMatched = items.at(-1)?.matches_expected === true;

  return (
    <div className="readback-reconciliation-card">
      {items.map((item) => {
        const presentation = readbackPresentation(item, finalMatched, negative, resourceType, intent);
        return <div key={item.readback_id} className={`readback-node ${presentation.tone}`}>
          <div className="readback-node-header">
            <span className="readback-status-icon">
              {presentation.tone === "matched" ? (
                <CheckCircle2 size={16} />
              ) : presentation.tone === "mismatch" ? (
                <XCircle size={16} />
              ) : (
                <Clock3 size={16} />
              )}
            </span>
          <div className="readback-node-meta">
              <strong>{presentation.title}</strong>
              <span className="status-divider">·</span>
              <span className={`status-pill pill-${presentation.tone}`}>
                {presentation.status}
              </span>
              <span className="status-divider">·</span>
              <time>{dateTime(item.observed_at)}</time>
            </div>
          </div>

          {item.observed_value != null && (
            <div className="readback-node-val">
              <CornerDownRight className="readback-val-icon" size={13} />
              <span className="readback-val-label">{negative ? "确认项:" : isPlacement ? "位置溢价读回:" : resourceType === "sp_campaigns" ? "预算读回:" : "读回值:"}</span>
              <code className="readback-val-code">
                <MicroValue value={negative ? negativeDisplayValue(item.observed_value, resourceType) : isPlacement ? formatPlacementDisplayValue(item.observed_value) : resourceType === "sp_campaigns" ? formatBudgetDisplayValue(item.observed_value) : bidOnlyValue(item.observed_value)} />
              </code>
            </div>
          )}

          {item.error_message && !finalMatched && (
            <div className="readback-error-message">
              <AlertTriangle size={14} />
              <span>
                {item.error_code ? `${item.error_code}: ` : ""}
                {item.error_message}
              </span>
            </div>
          )}
        </div>;
      })}
    </div>
  );
}

function TraceAccordionItem({
  icon,
  title,
  badge,
  data,
  timestamp,
}: {
  icon: ReactNode;
  title: string;
  badge: string;
  data: unknown;
  timestamp?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const hasData = data != null;

  return (
    <div className={`trace-item ${open ? "open" : ""} ${!hasData ? "empty" : ""}`}>
      <button
        type="button"
        className="trace-item-header"
        onClick={() => hasData && setOpen(!open)}
        disabled={!hasData}
        aria-expanded={open}
      >
        <span className="trace-chevron">
          {hasData ? (
            open ? <ChevronDown size={14} /> : <ChevronRight size={14} />
          ) : (
            <span className="trace-dot" />
          )}
        </span>
        <span className="trace-icon">{icon}</span>
        <span className="trace-title">{title}</span>
        {timestamp && <time className="trace-time">{dateTime(timestamp)}</time>}
        <span className="trace-badge status status-recorded">
          {badge}
        </span>
      </button>

      {open && hasData && (
        <div className="trace-item-body">
          <div className="dark-code-console">
            <div className="console-header">
              <span className="console-title">JSON PAYLOAD</span>
              <button
                type="button"
                className="console-copy-btn"
                onClick={() => {
                  void copyText(JSON.stringify(data, null, 2));
                }}
              >
                复制 JSON
              </button>
            </div>
            <pre className="console-pre">
              <code>{JSON.stringify(data, null, 2)}</code>
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

function ChangelogAccordionItem({
  entries,
}: {
  entries: MapChangelogEntry[];
}) {
  const [open, setOpen] = useState(false);
  if (!entries.length) return null;

  return (
    <div className={`trace-item ${open ? "open" : ""}`}>
      <button
        type="button"
        className="trace-item-header"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="trace-chevron">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        <span className="trace-icon">
          <FileCheck2 size={14} />
        </span>
        <span className="trace-title">
          MAP CHANGELOG <span className="trace-count">[{entries.length}]</span>
        </span>
        <time className="trace-time">{dateTime(entries[0]?.observed_at)}</time>
        <span className="trace-badge status status-recorded">
          {entries[0]?.status ?? "RECORDED"}
        </span>
      </button>

      {open && (
        <div className="trace-item-body">
          <div className="changelog-trace-entries">
            {entries.map((entry) => (
              <div key={entry.changelog_entry_id} className="changelog-entry-row">
                <div className="changelog-entry-header">
                  <span className="status status-simulated">{entry.status}</span>
                  <code className="changelog-id">
                    {entry.external_change_id ?? shortId(entry.changelog_entry_id)}
                  </code>
                  <time className="changelog-time">{dateTime(entry.observed_at)}</time>
                </div>
                {entry.payload != null && (
                  <div className="dark-code-console compact">
                    <pre className="console-pre">
                      <code>{JSON.stringify(entry.payload, null, 2)}</code>
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Failure({
  code,
  message,
}: {
  code: string | null;
  message: string | null;
}) {
  return (
    <div className="execution-failure">
      <AlertTriangle />
      <div>
        <strong>{code ?? "EXECUTION_FAILED"}</strong>
        <p>{message ?? "执行未完成"}</p>
      </div>
    </div>
  );
}
function AuditLoading() {
  return (
    <div className="audit-loading">
      <Clock3 />
      <span>正在读取审计数据</span>
    </div>
  );
}
function AuditEmpty({
  icon,
  title,
  detail,
}: {
  icon: ReactNode;
  title: string;
  detail: string;
}) {
  return (
    <section className="audit-empty">
      <span>{icon}</span>
      <h2>{title}</h2>
      <p>{detail}</p>
    </section>
  );
}
function compactValue(value: unknown): string {
  if (value == null) return "-";
  if (typeof value !== "object") return String(value);
  if (Array.isArray(value)) return value.map(compactValue).join(", ") || "[]";
  const entries = Object.entries(value as Record<string, unknown>);
  return entries.length
    ? entries
        .map(
          ([key, child]) =>
            `${key}: ${typeof child === "object" && child !== null ? JSON.stringify(child) : String(child)}`,
        )
        .join(" · ")
    : "{}";
}

function entityIdsForAction(action: OptimizationRecommendations["actions"][number]): string[] {
  const evidenceId = action.evidence
    ? Object.entries(action.evidence).find(([key]) => key.toLowerCase() === "entity_id")?.[1]
    : null;
  return [...new Set([action.entity?.entity_id, evidenceId]
    .filter((value): value is string | number => typeof value === "string" || typeof value === "number")
    .map(String)
    .filter(Boolean))];
}

export function withoutEntityIds(text: string, entityIds: Array<string | number | null | undefined>): string {
  let sanitized = text;
  for (const rawId of entityIds) {
    const entityId = String(rawId ?? "").trim();
    if (!entityId) continue;
    const escaped = entityId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    sanitized = sanitized
      .replace(new RegExp(`\\s*\\(?\\s*entity[_\\s-]*id\\s*[:=：]\\s*${escaped}\\s*\\)?`, "gi"), "")
      .replace(new RegExp(`\\s*[·|]\\s*ID\\s*[:=：]?\\s*${escaped}`, "gi"), "")
      .replace(new RegExp(`ID\\s*[:=：]?\\s*${escaped}`, "gi"), "")
      .replace(new RegExp(escaped, "g"), "");
  }
  return formatAcosText(sanitized.replace(/``/g, "").replace(/\(\s*\)/g, "").replace(/[ \t]{2,}/g, " ").trim());
}

/** Formats human-facing ACOS ratios while leaving formulas and persisted values untouched. */
export function formatAcosText(text: string): string {
  const ratioFormatted = text.replace(
    /\bACOS(?![A-Za-z0-9_])([ \t]*(?:(?:不高于|不低于|目标为|上限为|约为|达到|高于|低于|为|是)[ \t]*)?[:：]?[ \t]*)(0(?:\.\d+)?|1(?:\.0+)?)(?![\d.]|[ \t]*%)/gi,
    (_match, separator: string, rawValue: string) => `ACOS${separator}${(Number(rawValue) * 100).toFixed(2)}%`,
  );
  return ratioFormatted.replace(
    /\bACOS(?![A-Za-z0-9_])([ \t]*(?:(?:不高于|不低于|目标为|上限为|约为|达到|高于|低于|为|是)[ \t]*)?[:：]?[ \t]*)(\d+(?:\.\d+)?)[ \t]*%/gi,
    (_match, separator: string, percentage: string) => `ACOS${separator}${Number(percentage).toFixed(2)}%`,
  );
}

/**
 * Automatically sanitizes Markdown documents:
 * 1. Converts unrendered LaTeX math formulas ($$...$$ and $...$) into crisp, readable inline code blocks.
 * 2. Wraps raw ASCII box art (┌...└) in ```text ... ``` so they render with proper monospace font without breaking.
 * 3. Sanitizes Markdown tables to prevent pipe '|' characters inside entity names, bold spans, or code spans from being misparsed as column delimiters.
 */
export function sanitizeMarkdownTables(markdown: string): string {
  if (!markdown || typeof markdown !== "string") return "";

  // 1. Convert LaTeX math $$...$$ to readable inline code formulas
  let text = markdown.replace(/\$\$([\s\S]*?)\$\$/g, (_match, mathContent) => {
    let clean = mathContent.trim();
    clean = clean.replace(/\\text\{([^}]*)\}/g, "$1");
    clean = clean.replace(/\\times/g, "×");
    clean = clean.replace(/\\cdot/g, "·");
    clean = clean.replace(/\\le(?:q)?\b/g, "≤");
    clean = clean.replace(/\\ge(?:q)?\b/g, "≥");
    clean = clean.replace(/\\Longleftrightarrow\b/g, "⟺");
    clean = clean.replace(/\\iff\b/g, "⟺");
    clean = clean.replace(/\\Rightarrow\b/g, "⟹");
    clean = clean.replace(/\\rightarrow\b/g, "➔");
    clean = clean.replace(/\\approx\b/g, "≈");
    clean = clean.replace(/\\pm\b/g, "±");
    clean = clean.replace(/\\quad\b/g, "   ");
    clean = clean.replace(/\\qquad\b/g, "     ");
    clean = clean.replace(/\\,/g, " ");
    clean = clean.replace(/\\;/g, " ");
    clean = clean.replace(/\\\\/g, "\n");
    clean = clean.replace(/[{}]/g, "");
    clean = clean.replace(/\s+/g, " ").trim();
    return `\`${clean}\``;
  });

  // Also convert inline math $...$ if it contains LaTeX commands
  text = text.replace(/\$([^\$\n]+?)\$/g, (_match, mathContent) => {
    if (!/\\(?:text|times|le|ge|cdot|Longleftrightarrow|iff|Rightarrow|rightarrow|approx|quad)/.test(mathContent)) {
      return _match;
    }
    let clean = mathContent.trim();
    clean = clean.replace(/\\text\{([^}]*)\}/g, "$1");
    clean = clean.replace(/\\times/g, "×");
    clean = clean.replace(/\\cdot/g, "·");
    clean = clean.replace(/\\le(?:q)?\b/g, "≤");
    clean = clean.replace(/\\ge(?:q)?\b/g, "≥");
    clean = clean.replace(/\\Longleftrightarrow\b/g, "⟺");
    clean = clean.replace(/\\iff\b/g, "⟺");
    clean = clean.replace(/\\Rightarrow\b/g, "⟹");
    clean = clean.replace(/\\rightarrow\b/g, "➔");
    clean = clean.replace(/\\approx\b/g, "≈");
    clean = clean.replace(/\\pm\b/g, "±");
    clean = clean.replace(/\\quad\b/g, "   ");
    clean = clean.replace(/\\qquad\b/g, "     ");
    clean = clean.replace(/\\,/g, " ");
    clean = clean.replace(/\\;/g, " ");
    clean = clean.replace(/\\\\/g, "\n");
    clean = clean.replace(/[{}]/g, "");
    clean = clean.replace(/\s+/g, " ").trim();
    return `\`${clean}\``;
  });

  // 2. Wrap ASCII box art in ```text ... ``` if not already wrapped
  text = text.replace(/(?:^|\n)([┌├][^\n]*\n(?:[│├\s\S]*?)[└┘][^\n]*)/g, (_match, box) => {
    return `\n\`\`\`text\n${box.trim()}\n\`\`\`\n`;
  });

  // 3. Sanitize markdown tables
  const lines = text.split("\n");
  let inFencedCodeBlock = false;
  let expectedCols = 0;

  const processed = lines.map((line) => {
    const trimmed = line.trim();
    if (trimmed.startsWith("```")) {
      inFencedCodeBlock = !inFencedCodeBlock;
      expectedCols = 0;
      return line;
    }
    if (inFencedCodeBlock) {
      return line;
    }

    // Check if table delimiter row e.g. | :--- | :--- | ...
    const isSep = /^\|?\s*:?-+:?\s*(\|?\s*:?-+:?\s*)+\|?$/.test(trimmed);
    if (isSep) {
      const cells = trimmed.split("|").filter((c) => c.trim().length > 0);
      expectedCols = cells.length;
      return line;
    }

    // Check if there are table pipe delimiters OUTSIDE of inline code spans
    const nonCodePart = line.replace(/(`+)([\s\S]*?)\1/g, "");
    if (!nonCodePart.includes("|")) {
      expectedCols = 0;
      return line;
    }

    // Replace pipe '|' characters inside inline backticks `...`
    let result = line.replace(/(`+)([\s\S]*?)\1/g, (_match, delimiter, codeContent) => {
      if (typeof codeContent === "string" && codeContent.includes("|")) {
        return `${delimiter}${codeContent.replace(/\|/g, "&#124;")}${delimiter}`;
      }
      return _match;
    });

    // Replace pipe '|' characters inside bold **...**
    result = result.replace(/\*\*([^*]+?)\*\*/g, (_match, boldContent) => {
      if (typeof boldContent === "string" && boldContent.includes("|")) {
        return `**${boldContent.replace(/\|/g, "&#124;")}**`;
      }
      return _match;
    });

    // If expectedCols > 0 and row has more columns than expected, sanitize unescaped pipes in campaign names
    if (expectedCols > 0) {
      const rawCols = result.split("|").filter((_c, idx, arr) => {
        if (idx === 0 && arr[0].trim() === "") return false;
        if (idx === arr.length - 1 && arr[arr.length - 1].trim() === "") return false;
        return true;
      });

      if (rawCols.length > expectedCols) {
        // Replace internal campaign name pipes [ROLE] | COUNTRY | SP | SKU | TARGET
        result = result.replace(/(\[[A-Z0-9_\s\.\-]+\])([\s\S]*?)(\s*\|\s*(?:手动|自动|冲刺|赢家|常规|收割|精准|词组|广泛|竞品|已有|暂无|默认|€|[0-9]))/g,
          (_match, prefix, middle, suffix) => {
            return `${prefix}${middle.replace(/\|/g, "&#124;")}${suffix}`;
          }
        );
      }
    }

    return result;
  });

  return processed.join("\n");
}

function entityKindLabel(entity: ActionEntity | null | undefined): string {
  return entity?.entity_type === "keyword" ? "关键词" : entity?.entity_type === "target" ? "投放目标" : "广告实体";
}

function entityMetaLabel(entity: ActionEntity, resourceType?: string | null): string {
  if (entity.entity_type === "keyword") return entity.match_type ? `${entity.match_type} ${isNegativeResourceType(resourceType) ? "否定" : "匹配"}` : "关键词投放";
  if (entity.asin) return isNegativeResourceType(resourceType) ? "ASIN 否定" : "商品投放";
  if (entity.targeting_type && entity.targeting_type !== "TARGET") {
    if (isNegativeResourceType(resourceType) && (entity.targeting_type.includes("ASIN") || entity.targeting_type === "TARGET")) {
      return "ASIN 否定";
    }
    return automaticTargetLabel(entity.targeting_type) ?? `Target · ${entity.targeting_type}`;
  }
  return isNegativeResourceType(resourceType) ? "ASIN 否定" : "Target";
}
export function formatMoneyValue(value: unknown, symbol: string = ""): string {
  if (value == null || value === "") return "-";
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${symbol}${numeric.toFixed(2)}` : String(value);
}

export function getCurrencySymbol(currency?: string | null): string {
  if (!currency) return "";
  const code = String(currency).trim().toUpperCase();
  switch (code) {
    case "GBP":
      return "£";
    case "EUR":
      return "€";
    case "USD":
      return "$";
    case "JPY":
      return "¥";
    case "CAD":
      return "CA$";
    case "AUD":
      return "AU$";
    default:
      return code.length <= 3 ? code : "";
  }
}

function resolveCurrencySymbol(
  currency?: string | null,
  evidence?: Record<string, unknown> | null,
  campaignName?: string | null,
  decisionReason?: string | null
): string {
  if (currency) {
    const sym = getCurrencySymbol(currency);
    if (sym) return sym;
  }
  const evCurrency = evidence?.currency ?? evidence?.CURRENCY;
  if (typeof evCurrency === "string") {
    const sym = getCurrencySymbol(evCurrency);
    if (sym) return sym;
  }
  if (decisionReason) {
    if (decisionReason.includes("£")) return "£";
    if (decisionReason.includes("€")) return "€";
    if (decisionReason.includes("$")) return "$";
    if (decisionReason.includes("¥")) return "¥";
  }
  if (campaignName) {
    const tokens = campaignName.toUpperCase().split(/[\s|\[\]_()\-,\.]+/).filter(Boolean);
    if (tokens.includes("UK") || tokens.includes("GB")) return "£";
    if (tokens.some((t) => ["DE", "FR", "IT", "ES", "EU", "NL", "PL", "SE", "BE"].includes(t))) return "€";
    if (tokens.includes("US")) return "$";
    if (tokens.includes("CA")) return "CA$";
    if (tokens.includes("JP")) return "¥";
    if (tokens.includes("AU")) return "AU$";
  }
  return "";
}
function formatWindowValue(value: unknown, start: unknown, end: unknown): string {
  const label = value == null || value === "" ? "当前数据" : String(value);
  const startText = typeof start === "string" && /^\d{4}-\d{2}-\d{2}$/.test(start) ? start : null;
  const endText = typeof end === "string" && /^\d{4}-\d{2}-\d{2}$/.test(end) ? end : null;
  return startText && endText ? `${label}（${startText} 至 ${endText}）` : label;
}
function shortId(value?: string | null) {
  return value ? value.split("-").slice(-1)[0].slice(0, 8) : "-";
}
function shortHash(value?: string | null) {
  return value ? value.slice(0, 12) : "-";
}
function modeLabel(mode: string) {
  return mode === "SIMULATED"
    ? "模拟"
    : mode === "LIVE"
      ? "真实 MAP"
      : "待执行";
}
function dateTime(value?: string | null) {
  return value
    ? new Intl.DateTimeFormat("zh-CN", {
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(new Date(value))
    : "-";
}
